// REAL PATCH GENERATED 2026-03-21 13:18
// src/cashup/PayrollTab.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { collection, getDocs, where, query as q, doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useOrgId, useOrgMeta } from '../utils/tenant';
import Modal from '../components/Modal';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CSVLink } from 'react-csv';
import PayrollFoundationPanel from './PayrollFoundationPanel';
import PayrollStatutoryPreview from './PayrollStatutoryPreview';
import PayrollForecastPanel from './PayrollForecastPanel';
import PayrollReconciliationPanel from './PayrollReconciliationPanel';
import PayrollReadinessPanel from './PayrollReadinessPanel';
import PayrollClassificationPreparationPanel from './PayrollClassificationPreparationPanel';
import PayrollExceptionReviewDrawer from './PayrollExceptionReviewDrawer';
import PayrollCarryForwardPanel from './PayrollCarryForwardPanel';
import { calculateDraftEarnings, DEFAULT_EARNING_RULES, LINE_ITEM_CATEGORIES } from './utils/payrollEarnings';
import { SPECIAL_EARNING_CLASSIFICATIONS } from './utils/payrollSpecialEarnings';
import { buildPayrollForecast } from './utils/payrollForecast';

/* ===== react-select dark theme ===== */
const selectTheme = (t) => ({
  ...t,
  colors: {
    ...t.colors,
    primary25: '#334155',
    primary: '#1e293b',
    neutral0: '#1e293b',
    neutral80: '#fff',
  },
});
const selectStyles = {
  control: (b) => ({ ...b, backgroundColor: '#1e293b', borderColor: '#334155', color: '#fff' }),
  menu: (b) => ({ ...b, backgroundColor: '#1e293b' }),
  option: (b) => ({ ...b, backgroundColor: '#1e293b', color: '#fff' }),
};

/* ===== helpers ===== */
const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;

const toSec = (v) =>
  v?.seconds ??
  (v?.toDate ? Math.floor(v.toDate().getTime() / 1000) : typeof v === 'number' ? v : null);


const approvedShiftSeconds = (shiftText, rawClockInSec) => {
  const match = String(shiftText || '').match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (!match || typeof rawClockInSec !== 'number') return { start: null, end: null };

  const base = new Date(rawClockInSec * 1000);
  const start = new Date(base);
  start.setHours(Number(match[1]), Number(match[2]), 0, 0);

  const end = new Date(base);
  end.setHours(Number(match[3]), Number(match[4]), 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);

  return {
    start: Math.floor(start.getTime() / 1000),
    end: Math.floor(end.getTime() / 1000),
  };
};

const normalizeShiftDoc = (snap, pathArray, storeIdFromPath) => {
  const d = snap.data() || {};
  const rawCi = toSec(d.clockIn) ?? toSec(d.clockInAt) ?? toSec(d.in) ?? toSec(d.inAt);
  const rawCo = toSec(d.clockOut) ?? toSec(d.clockOutAt) ?? toSec(d.out) ?? toSec(d.outAt);
  const approved = d.scheduleVarianceApproved === true;
  const approvedFromShift = approvedShiftSeconds(d.approvedScheduleShift, rawCi);
  const approvedCi =
    toSec(d.approvedActualClockInSec) ??
    (approved ? approvedFromShift.start : null);
  const approvedCo =
    toSec(d.approvedActualClockOutSec) ??
    (approved ? approvedFromShift.end : null);

  // Phase 4: Payroll consumes the same immutable actual clock evidence as Labor.
  // Approval changes classification, never the recorded in/out times.
  const ci = rawCi;
  const co = rawCo;
  const oci = toSec(d.oldClockIn);
  const oco = toSec(d.oldClockOut);
  const actualCo = toSec(d.actualClockOut);
  const thresholdCo = toSec(d.thresholdClockOut);

  return {
    id: snap.id,
    name: d.name || d.staffName || d.employeeName || '',
    staffNameSnapshot: d.staffNameSnapshot || d.staffName || d.name || d.employeeName || '',
    staffId: d.staffId || d.userId || d.uid || '',
    role: d.role || d.roleName || '',
    store: d.store || d.storeId || storeIdFromPath || '',
    clockIn: ci != null ? { seconds: ci } : null,
    clockOut: co != null ? { seconds: co } : null,
    oldClockIn: oci != null ? { seconds: oci } : undefined,
    oldClockOut: oco != null ? { seconds: oco } : undefined,
    actualClockOut: actualCo != null ? { seconds: actualCo } : undefined,
    thresholdClockOut: thresholdCo != null ? { seconds: thresholdCo } : undefined,
    autoClockedOutAtThreshold: d.autoClockedOutAtThreshold === true,
    lunchMin:
      typeof d.lunchMin === 'number'
        ? d.lunchMin
        : typeof d.lunchMinutes === 'number'
          ? d.lunchMinutes
          : typeof d.lunch === 'number'
            ? d.lunch
            : null,
    reason: d.reason || '',
    archived: !!(d.archived || d.isArchived),
    archivedAt: d.archivedAt || null,
    archivedBy: d.archivedBy || d.archivedByEmail || d.archivedByUid || '',
    editedBy: d.editedBy || d.updatedBy || d.modifiedBy || '',
    editedAt: d.editedAt || d.updatedAt || null,
    scheduleVarianceApproved: d.scheduleVarianceApproved === true,
    scheduleVarianceRejected: d.scheduleVarianceRejected === true,
    payrollZeroHours: d.payrollZeroHours === true,
    scheduleVarianceType: d.scheduleVarianceType || '',
    swapExpectedStaffId: d.swapExpectedStaffId || '',
    swapExpectedStaffName: d.swapExpectedStaffName || '',
    approvedScheduleShift: d.approvedScheduleShift || '',
    approvedActualClockInSec: approvedCi,
    approvedActualClockOutSec: approvedCo,
    __path: pathArray,
  };
};



const readPayrollStorage = (key, fallback = '') => {
  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

const readPayrollJson = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw || raw.length > 250000) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const cleanupOversizedPayrollStorage = () => {
  try {
    const keys = [
      'payroll_store',
      'payroll_totalHours',
    ];
    keys.forEach((key) => {
      const value = window.localStorage.getItem(key);
      if (value && value.length > 250000) window.localStorage.removeItem(key);
    });
  } catch {}
};

const setPayrollStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    try {
      cleanupOversizedPayrollStorage();
      window.localStorage.setItem(key, value);
    } catch (retryError) {
      console.warn(`Payroll preference ${key} could not be saved`, retryError);
    }
  }
};

const DEFAULT_ABANDONED_SHIFT_HOURS = 18;

const endOfDayMsForClockIn = (ciSeconds) => {
  const d = new Date(ciSeconds * 1000);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};

const isLegacyEndOfDayAutoOut = (shift, abandonedHours = 18) => {
  const ciSec = toSec(shift?.clockIn);
  const coSec = toSec(shift?.clockOut);
  const actualSec = toSec(shift?.actualClockOut);
  const thresholdSec = toSec(shift?.thresholdClockOut);

  if (typeof ciSec !== 'number' || typeof coSec !== 'number') return false;
  if (typeof actualSec === 'number' || typeof thresholdSec === 'number' || shift?.autoClockedOutAtThreshold) return false;

  const ci = new Date(ciSec * 1000);
  const co = new Date(coSec * 1000);

  const sameCalendarDay =
    ci.getFullYear() === co.getFullYear() &&
    ci.getMonth() === co.getMonth() &&
    ci.getDate() === co.getDate();

  if (!(sameCalendarDay && co.getHours() === 23 && co.getMinutes() === 59)) return false;

  const rawHours = (coSec - ciSec) / 3600;
  return rawHours > Math.max(1, Number(abandonedHours) || 18);
};

const getThresholdCutoffSec = (shift, abandonedHours = 18) => {
  const ciSec = toSec(shift?.clockIn);
  if (typeof ciSec !== 'number') return null;
  return ciSec + Math.max(1, Number(abandonedHours) || 18) * 3600;
};

const getDisplayClockOutSec = (shift, abandonedHours = 18) => {
  const coSec = toSec(shift?.clockOut);
  if (typeof coSec === 'number') return coSec;

  const actualSec = toSec(shift?.actualClockOut);
  if (typeof actualSec === 'number') return actualSec;

  // Compatibility only for historical records that retained approval metadata
  // but lost the raw final clock-out.
  const approvedOutSec = toSec(shift?.approvedActualClockOutSec);
  if (typeof approvedOutSec === 'number') return approvedOutSec;

  return null;
};

const buildClockOutDate = (ciDate, shift, abandonedHours = 18) => {
  const outSec = getDisplayClockOutSec(shift, abandonedHours);
  if (typeof outSec !== 'number') {
    const d = new Date(ciDate);
    d.setHours(23, 59, 0, 0);
    return d;
  }
  let co = new Date(outSec * 1000);
  if (co < ciDate) {
    co = new Date(co.getTime() + 24 * 3600000);
  }
  return co;
};

const dedupePayrollRows = (items, getEffectiveOutSec = null) => {
  const map = new Map();

  const minuteBucket = (sec) => {
    if (!sec) return '';
    const d = new Date(sec * 1000);
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
      String(d.getHours()).padStart(2, '0'),
      String(d.getMinutes()).padStart(2, '0'),
    ].join('-');
  };

  for (const s of items || []) {
    const ciSec = s?.clockIn?.seconds || null;
    if (!ciSec) continue;

    const effectiveOutSec = typeof getEffectiveOutSec === 'function'
      ? getEffectiveOutSec(s)
      : getDisplayClockOutSec(s, DEFAULT_ABANDONED_SHIFT_HOURS) ?? Math.floor(endOfDayMsForClockIn(ciSec) / 1000);

    const key = [
      String(s.name || '').trim().toLowerCase(),
      String(s.role || '').trim().toLowerCase(),
      String(s.store || '').trim().toLowerCase(),
      minuteBucket(ciSec),
      minuteBucket(effectiveOutSec),
    ].join('|');

    const existing = map.get(key);
    if (!existing) {
      map.set(key, s);
      continue;
    }

    const existingEdited = Boolean(existing.oldClockIn || existing.oldClockOut || existing.reason || existing.editedAt || existing.editedBy);
    const currentEdited = Boolean(s.oldClockIn || s.oldClockOut || s.reason || s.editedAt || s.editedBy);

    const existingHasRealOut = Boolean(existing.clockOut?.seconds);
    const currentHasRealOut = Boolean(s.clockOut?.seconds);

    const existingPathLen = Array.isArray(existing.__path) ? existing.__path.length : 0;
    const currentPathLen = Array.isArray(s.__path) ? s.__path.length : 0;

    const existingClockInSec = existing.clockIn?.seconds || 0;
    const currentClockInSec = s.clockIn?.seconds || 0;

    if (
      (!existingEdited && currentEdited) ||
      (!existingHasRealOut && currentHasRealOut) ||
      (currentPathLen > existingPathLen) ||
      (currentClockInSec > existingClockInSec)
    ) {
      map.set(key, s);
    }
  }

  return Array.from(map.values()).sort(
    (a, b) => (a.clockIn?.seconds || 0) - (b.clockIn?.seconds || 0)
  );
};

const num = (v, f = 2) => (Number.isFinite(+v) ? Number(v).toFixed(f) : (0).toFixed(f));
const asMoney = (v) => (Number.isFinite(+v) ? +Number(v).toFixed(2) : 0);

// stable key for staff name (prevents “clearing” on refresh if whitespace differs)
const staffKey = (name) => String(name || '').trim().replace(/\s+/g, ' ');
const localDay = d => d instanceof Date && !Number.isNaN(d.getTime()) ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : '';
// Determine today's pay eligibility date in the configured employer timezone.
// Future published leave is forecast, NOT yet earned-to-date.
const payrollDayInZone = (date, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: timeZone || 'Africa/Johannesburg', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
    const part = key => parts.find(p => p.type === key)?.value || '';
    return `${part('year')}-${part('month')}-${part('day')}`;
  } catch { return localDay(date); }
};



const localDateKeyFromSec = (sec) => {
  if (typeof sec !== 'number') return '';
  const d = new Date(sec * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const scheduleBoundarySeconds = (dateKey, shiftText) => {
  const match = String(shiftText || '').match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (!match || !dateKey) return { start: null, end: null };

  const [year, month, day] = dateKey.split('-').map(Number);
  const start = new Date(year, month - 1, day, Number(match[1]), Number(match[2]), 0, 0);
  const end = new Date(year, month - 1, day, Number(match[3]), Number(match[4]), 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);

  return {
    start: Math.floor(start.getTime() / 1000),
    end: Math.floor(end.getTime() / 1000),
  };
};

/* =================== COMPONENT =================== */
export default function PayrollTab({ siteScope = { allSites: true, siteIds: [] }, backPath='/cashup/profile' }) {
  const navigate = useNavigate();
  const [activePayrollTab, setActivePayrollTab] = useState('Overview');
  const [deductionsSubTab, setDeductionsSubTab] = useState('items');
  const [periodLoading, setPeriodLoading] = useState(false);
  const [loadedFingerprint,setLoadedFingerprint]=useState('');
  const [periodMessage, setPeriodMessage] = useState('Select a site and period, then load attendance.');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const orgId = useOrgId();
  const { orgName } = useOrgMeta();
  const csvRef = useRef();

  // ---------- public holidays (org settings) ----------
  const [publicPayEnabled, setPublicPayEnabled] = useState(true);
  const [publicHolidays, setPublicHolidays] = useState(() => new Set());
  const [orgLunchMin,setOrgLunchMin] = useState(60);
  const [orgAbandonedShiftHours, setOrgAbandonedShiftHours] = useState(DEFAULT_ABANDONED_SHIFT_HOURS);
  const [storeAbandonedShiftHours, setStoreAbandonedShiftHours] = useState({});

  useEffect(() => {
    if (!orgId) return;
    const ref = doc(db, 'orgs', orgId, 'settings', 'labor');
    // C8.6: payroll configuration is fresh-on-open, not realtime.
    getDoc(ref).then((snap) => {
      const d = snap.data() || {};
      setPublicPayEnabled(d.publicPayEnabled !== false);
      const arr = Array.isArray(d.publicHolidays) ? d.publicHolidays : [];
      setPublicHolidays(new Set(arr));
      const rawLunch=Number(d.lunchMin ?? d.lunchMinutes ?? 60);
      setOrgLunchMin(Number.isFinite(rawLunch) && rawLunch>=0 && rawLunch<=240?rawLunch:60);
      const threshold = Number(d.forceAutoClockOutHours ?? d.abandonedShiftHours);
      setOrgAbandonedShiftHours(
        Math.max(18, Math.min(24, Number.isFinite(threshold) ? Math.round(threshold) : DEFAULT_ABANDONED_SHIFT_HOURS))
      );
     }).catch((error) => {
      console.error('[Payroll] Failed to load labor settings', error);
    });
  }, [orgId]);

  // ---------- persistent filters ----------
  const [dateFrom, setDateFrom] = useState(() => {
    const s = readPayrollStorage('payroll_from');
    if (s) { const d = new Date(s); if (!isNaN(d)) return d; }
    const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d;
  });
  const [dateTo, setDateTo] = useState(() => {
    const s = readPayrollStorage('payroll_to');
    if (s) { const d = new Date(s); if (!isNaN(d)) return d; }
    const d = new Date(); d.setHours(23,59,59,999); return d;
  });

  // 180 is the default until the protected employer payroll configuration loads.
  // A browser setting must not override the Owner's payroll calculation policy.
  const [totalHours, setTotalHours] = useState(180);

  // Payroll target hours remain a salary/rate reference only. Overtime comes from Schedule Intelligence approval.
  const payrollTargetHours = useMemo(() => {
    const v = Number(totalHours);
    return Number.isFinite(v) ? v : 0;
  }, [totalHours]);

  const [stores, setStores] = useState([{ value: 'ALL', label: 'All stores' }]);
  const [store, setStore] = useState(() => {
    try {
      cleanupOversizedPayrollStorage();
      const savedId = readPayrollStorage('payroll_store_id');
      const savedLabel = readPayrollStorage('payroll_store_label');
      if (savedId) return { value: savedId, label: savedLabel || (savedId === 'ALL' ? 'All stores' : savedId) };
      const saved = readPayrollStorage('payroll_store');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          return { value: parsed.value || parsed.id || 'ALL', label: parsed.label || parsed.name || parsed.storeName || 'All stores' };
        }
      }
    } catch {}
    return { value: 'ALL', label: 'All stores' };
  });

  const selectedFingerprint=`${orgId||''}|${store?.value||'ALL'}|${localDay(dateFrom)}|${localDay(dateTo)}`;
  const periodIsCurrent=loadedFingerprint===selectedFingerprint;

  // ---------- per-staff inputs (persist across refresh) ----------
  // Sensitive pay data comes from the protected PR1 endpoint, never browser localStorage.
  const [earningRules, setEarningRules] = useState(DEFAULT_EARNING_RULES);
  const [payrollCurrency, setPayrollCurrency] = useState('ZAR');
  const [payrollTimeZone, setPayrollTimeZone] = useState('Africa/Johannesburg');
  const [payrollDataScope, setPayrollDataScope] = useState('');
  const [profilesByKey, setProfilesByKey] = useState({});
  const [draftItems, setDraftItems] = useState({});
  const [retirementDrafts, setRetirementDrafts] = useState({});
  const saveItemisedRef = useRef(null);
  const saveContractualReviewRef = useRef(null);
  const saveTaxProfileRef = useRef(null);
  const saveRetirementDraftRef = useRef(null);
  const previewStatutoryRef = useRef(null);
  const previewForecastSnapshotRef = useRef(null);
  const lockForecastSnapshotRef = useRef(null);
  const previewForecastAmendmentRef = useRef(null);
  const commitForecastAmendmentRef = useRef(null);
  const getForecastSnapshotRef = useRef(null);
  const reconcileForecastSnapshotRef = useRef(null);
  const getFinalPayrollReadinessRef = useRef(null);
  const listFinancialClassificationCasesRef = useRef(null);
  const prepareFinancialClassificationCaseRef = useRef(null);
  const escalateFinancialClassificationCaseRef = useRef(null);
  const listFinancialClassificationSourcesRef = useRef(null);
  const previewFinancialClassificationSourceRef = useRef(null);
  const submitFinancialClassificationAssessmentRef = useRef(null);
  const reviewFinancialClassificationAssessmentRef = useRef(null);
  const listIndependentPayrollReviewersRef=useRef(null);
  const grantIndependentPayrollReviewerRef=useRef(null);
  const revokeIndependentPayrollReviewerRef=useRef(null);
  const getExceptionEvidenceRef = useRef(null);
  const requestOperationalVerificationRef=useRef(null);
  const listOperationalVerificationRef=useRef(null);
  const listCarryForwardCasesRef = useRef(null);
  const getCarryForwardAuditRef = useRef(null);
  const proposeCarryForwardRef = useRef(null);
  const reviewCarryForwardCaseRef = useRef(null);
  const markCarryForwardIncludedRef = useRef(null);
  const recordCarryForwardSettlementRef = useRef(null);
  const [payrollIsOwner,setPayrollIsOwner] = useState(false);
  const [payrollCanDelegate,setPayrollCanDelegate]=useState(false);
  const [contractualReviews,setContractualReviews] = useState({});
  const [contractualReviewInput,setContractualReviewInput] = useState({status:'approved',reason:''});
  const [contractualReviewBusy,setContractualReviewBusy] = useState(false);
  const [contractualReviewMessage,setContractualReviewMessage] = useState('');
  const [itemModal, setItemModal] = useState({open:false,row:null,items:[]});
  const [itemSaving, setItemSaving] = useState(false);
  const [itemError, setItemError] = useState('');
  const [salary, setSalary] = useState({});
  const [adj, setAdj] = useState({});
  const [note, setNote] = useState({});
  const [payrollUnlocked, setPayrollUnlocked] = useState(false);
  const [payrollCanEdit, setPayrollCanEdit] = useState(false);
  const saveDraftRef = useRef(null);
  const onFinancialState = useCallback((update={}) => {
    if ('saveAdjustment' in update) saveDraftRef.current = update.saveAdjustment;
    if ('saveItemisedDraft' in update) saveItemisedRef.current = update.saveItemisedDraft;
    if ('saveContractualExcessReview' in update) saveContractualReviewRef.current = update.saveContractualExcessReview;
    if ('saveTaxProfile' in update) saveTaxProfileRef.current = update.saveTaxProfile;
    if ('saveRetirementDraft' in update) saveRetirementDraftRef.current = update.saveRetirementDraft;
    if ('previewStatutory' in update) previewStatutoryRef.current = update.previewStatutory;
    if ('previewForecastSnapshot' in update) previewForecastSnapshotRef.current = update.previewForecastSnapshot;
    if ('lockForecastSnapshot' in update) lockForecastSnapshotRef.current = update.lockForecastSnapshot;
    if ('previewForecastAmendment' in update) previewForecastAmendmentRef.current = update.previewForecastAmendment;
    if ('commitForecastAmendment' in update) commitForecastAmendmentRef.current = update.commitForecastAmendment;
    if ('getForecastSnapshot' in update) getForecastSnapshotRef.current = update.getForecastSnapshot;
    if ('reconcileForecastSnapshot' in update) reconcileForecastSnapshotRef.current = update.reconcileForecastSnapshot;
    if ('getFinalPayrollReadiness' in update) getFinalPayrollReadinessRef.current = update.getFinalPayrollReadiness;
    if ('listFinancialClassificationCases' in update) listFinancialClassificationCasesRef.current = update.listFinancialClassificationCases;
    if ('prepareFinancialClassificationCase' in update) prepareFinancialClassificationCaseRef.current = update.prepareFinancialClassificationCase;
    if ('escalateFinancialClassificationCase' in update) escalateFinancialClassificationCaseRef.current = update.escalateFinancialClassificationCase;
    if ('listFinancialClassificationSources' in update) listFinancialClassificationSourcesRef.current = update.listFinancialClassificationSources;
    if ('previewFinancialClassificationSource' in update) previewFinancialClassificationSourceRef.current = update.previewFinancialClassificationSource;
    if ('submitFinancialClassificationAssessment' in update) submitFinancialClassificationAssessmentRef.current = update.submitFinancialClassificationAssessment;
    if ('reviewFinancialClassificationAssessment' in update) reviewFinancialClassificationAssessmentRef.current = update.reviewFinancialClassificationAssessment;
    if ('listIndependentPayrollReviewers' in update) listIndependentPayrollReviewersRef.current=update.listIndependentPayrollReviewers;
    if ('grantIndependentPayrollReviewer' in update) grantIndependentPayrollReviewerRef.current=update.grantIndependentPayrollReviewer;
    if ('revokeIndependentPayrollReviewer' in update) revokeIndependentPayrollReviewerRef.current=update.revokeIndependentPayrollReviewer;
    if ('getExceptionEvidence' in update) getExceptionEvidenceRef.current = update.getExceptionEvidence;
    if ('requestOperationalVerification' in update) requestOperationalVerificationRef.current=update.requestOperationalVerification;
    if ('listOperationalVerification' in update) listOperationalVerificationRef.current=update.listOperationalVerification;
    if ('listCarryForwardCases' in update) listCarryForwardCasesRef.current = update.listCarryForwardCases;
    if ('getCarryForwardAudit' in update) getCarryForwardAuditRef.current = update.getCarryForwardAudit;
    if ('proposeCarryForward' in update) proposeCarryForwardRef.current = update.proposeCarryForward;
    if ('reviewCarryForwardCase' in update) reviewCarryForwardCaseRef.current = update.reviewCarryForwardCase;
    if ('markCarryForwardIncluded' in update) markCarryForwardIncludedRef.current = update.markCarryForwardIncluded;
    if ('recordCarryForwardSettlement' in update) recordCarryForwardSettlementRef.current = update.recordCarryForwardSettlement;
    if ('isOwner' in update) setPayrollIsOwner(update.isOwner===true);
    if ('canDelegate' in update) setPayrollCanDelegate(update.canDelegate===true);
    if ('contractualReviews' in update) setContractualReviews(update.contractualReviews || {});
    if (update.contractualReviewEntry) setContractualReviews(old=>({...old,[update.contractualReviewEntry.key]:update.contractualReviewEntry.review}));
    if ('earningRules' in update) setEarningRules(update.earningRules || DEFAULT_EARNING_RULES);
    if ('currency' in update) setPayrollCurrency(update.currency || 'ZAR');
    if ('timeZone' in update) setPayrollTimeZone(update.timeZone || 'Africa/Johannesburg');
    if ('payrollDataScope' in update) setPayrollDataScope(update.payrollDataScope || '');
    if ('profilesByKey' in update) setProfilesByKey(update.profilesByKey || {});
    if (update.taxProfileEntry) setProfilesByKey(prev=>({...prev,[update.taxProfileEntry.key]:{...(prev[update.taxProfileEntry.key]||{}),...update.taxProfileEntry.profile}}));
    if ('draftItems' in update) setDraftItems(update.draftItems || {});
    if ('retirementDrafts' in update) setRetirementDrafts(update.retirementDrafts || {});
    if (update.retirementDraftEntry) setRetirementDrafts(prev=>({...prev,[update.retirementDraftEntry.key]:update.retirementDraftEntry.value}));
    if (update.draftItemsEntry) setDraftItems(prev=>({...prev,[update.draftItemsEntry.key]:update.draftItemsEntry.items||[]}));
    if ('unlocked' in update && update.unlocked === false) { setPayrollCanDelegate(false); setExceptionReviewKey(null);setDraftItems({});setRetirementDrafts({});setProfilesByKey({});setContractualReviews({});setPayrollDataScope('');setItemModal({open:false,row:null,items:[]}); }
    if ('unlocked' in update) setPayrollUnlocked(update.unlocked === true);
    if ('canEdit' in update) setPayrollCanEdit(update.canEdit === true);
    if (update.salaries) setSalary(update.salaries);
    if (update.adjustments) setAdj(update.adjustments);
    if (update.notes) setNote(update.notes);
    if (update.salaryEntry) setSalary(prev=>({...prev,[update.salaryEntry.key]:update.salaryEntry.value}));
    if (Number(update.referenceHours)>0) setTotalHours(Number(update.referenceHours));
  },[]);

  useEffect(() => setPayrollStorage('payroll_from', dateFrom?.toISOString() || ''), [dateFrom]);
  useEffect(() => setPayrollStorage('payroll_to', dateTo?.toISOString() || ''), [dateTo]);
  useEffect(() => {
    try {
      const safeStore = { value: store?.value || 'ALL', label: store?.label || 'All stores' };
      setPayrollStorage('payroll_store_id', safeStore.value);
      setPayrollStorage('payroll_store_label', safeStore.label);
      try { window.localStorage.removeItem('payroll_store'); } catch {}
    } catch (error) {
      console.warn('Payroll store preference could not be saved', error);
    }
  }, [store]);
  // Old localStorage salary/adjustment keys remain untouched for deliberate migration.
  // They are no longer read, written, or exposed in this Payroll tab.

  // ---------- data ----------
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  // Stable reference prevents refetch/rerender loops while filters are not loaded.
  const EMPTY_PAYROLL_SUMMARY = useRef([]).current;
  const securePayrollSummary = useMemo(
    () => periodIsCurrent ? summary : EMPTY_PAYROLL_SUMMARY,
    [periodIsCurrent, summary, EMPTY_PAYROLL_SUMMARY]
  );
  const [publishedScheduleMap, setPublishedScheduleMap] = useState(() => new Map());
  const [publishedAttendanceRows, setPublishedAttendanceRows] = useState([]);
  const [publishedForecastEntries, setPublishedForecastEntries] = useState([]);
  const [scheduleEvidenceReady, setScheduleEvidenceReady] = useState(false);
  const [acceptedPayrollDays, setAcceptedPayrollDays] = useState([]);
  const [forecastCutoff, setForecastCutoff] = useState('');
  const [earningsDetail, setEarningsDetail] = useState(null);
  const [exceptionReviewKey, setExceptionReviewKey] = useState(null);
  const [payrollDetailModal, setPayrollDetailModal] = useState({
    open: false,
    key: '',
    name: '',
    adjustment: '',
    note: '',
  });

  const storeIdToNameRef = useRef({});
  const storeNameToIdRef = useRef({});

  // PR2: never let a payroll officer's browser-local lunch preference change payable hours.
  // Per-shift recorded lunch minutes take priority; otherwise use organisation labour settings.
  const lunchMin = orgLunchMin;

  /* ---- Load stores once ---- */
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const storesCol = collection(db, 'orgs', orgId, 'cashup_stores');
      const snap = await getDocs(storesCol);
      const raw = snap.docs.map((d) => ({ id: d.id, name: String(d.data()?.name || d.id).trim(), isArchived: !!d.data()?.isArchived || !!d.data()?.archived }));
      const seen = new Set();
      const uniq = [];
      for (const s of raw.sort((a,b) => a.name.localeCompare(b.name))) {
        if (s.isArchived) continue;
        const k = s.name.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        uniq.push(s);
      }
      const idToName = {}; const nameToId = {};
      uniq.forEach((s) => { idToName[s.id] = s.name; nameToId[s.name] = s.id; });
      storeIdToNameRef.current = idToName;
      storeNameToIdRef.current = nameToId;

      const allowedSiteIds = new Set((siteScope.siteIds || []).map(String));
      const scopedStores = siteScope.allSites ? uniq : uniq.filter((s) => allowedSiteIds.has(String(s.id)));
      const scopedOptions = [
        ...(scopedStores.length > 1 ? [{ value: 'ALL', label: 'All stores' }] : []),
        ...scopedStores.map(s => ({ value: s.id, label: s.name })),
      ];
      setStores(scopedOptions);
      if (!siteScope.allSites && scopedStores.length === 1) {
        setStore({ value: scopedStores[0].id, label: scopedStores[0].name });
      } else if (store.value !== 'ALL' && !scopedStores.find(x => x.id === store.value)) {
        setStore(scopedOptions[0] || { value: 'ALL', label: 'All stores' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'orgs', orgId, 'cashup_stores'));
        const next = {};
        snap.docs.forEach((d) => {
          const raw = Number(d.data()?.forceAutoClockOutHours ?? d.data()?.abandonedShiftHours);
          if (Number.isFinite(raw)) next[d.id] = Math.max(18, Math.min(24, Math.round(raw)));
        });
        setStoreAbandonedShiftHours(next);
      } catch {
        setStoreAbandonedShiftHours({});
      }
    })();
  }, [orgId]);


  const resolveShiftThresholdHours = useCallback((shift) => {
    const rawStore = String(shift?.store || '').trim();
    const storeId = storeNameToIdRef.current[rawStore] || rawStore;
    const raw = storeAbandonedShiftHours[storeId] ?? orgAbandonedShiftHours;
    return Math.max(18, Math.min(24, Number(raw) || DEFAULT_ABANDONED_SHIFT_HOURS));
  }, [storeAbandonedShiftHours, orgAbandonedShiftHours]);

  const getEffectiveOutSec = useCallback((shift) => {
    const thresholdHours = resolveShiftThresholdHours(shift);
    return getDisplayClockOutSec(shift, thresholdHours);
  }, [resolveShiftThresholdHours]);

  // Published Schedule Intelligence roster is Payroll's regular-time boundary.
  // This mirrors Labor so Payroll never rebuilds payable hours from raw duration alone.
  useEffect(() => {
    if (!orgId) {
      setPublishedScheduleMap(new Map());
      setPublishedForecastEntries([]);
      setScheduleEvidenceReady(false);
      return undefined;
    }

    return onSnapshot(
      collection(db, 'orgs', orgId, 'neo_schedule_entries'),
      (snap) => {
        const next = new Map();
        const attendanceRows = [];
        const forecastEntries = [];
        snap.docs.forEach((scheduleDoc) => {
          const data = scheduleDoc.data() || {};
          if (!data.storeId || !data.date) return;
          if (data.published === false) return; // Explicit drafts are not regular-time payroll evidence.

          const publishedShift = String(
            (data.varianceApproved === true && data.originalPublishedShift
              ? data.originalPublishedShift
              : data.publishedShift) || ''
          );
          const storeKey = String(data.storeId);
          const dateKey = String(data.date);
          // PR3.6A: use exactly the published schedule evidence, not drafts.
          if (data.published === true) forecastEntries.push({sourceId:scheduleDoc.id,
            storeId:storeKey, date:dateKey, staffId:String(data.staffId||''), staffName:String(data.staffName||data.name||''),
            published:true, publishedShift, isAttendance:data.isAttendance===true, attendancePaid:data.attendancePaid===true,
            attendanceAllocateHours:data.attendanceAllocateHours===true, attendanceHours:data.attendanceHours,
            scheduledNetHours:data.scheduledNetHours, lunchBreakMinutes:data.lunchBreakMinutes,
          });
          const staffIdKey = String(data.staffId || '');
          const staffNameKey = String(data.staffName || data.name || '').trim().toLowerCase();

          if (!/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(publishedShift)) {
            const isAttendance = data.isAttendance === true || publishedShift.startsWith('ATTENDANCE::');
            const hours = Math.max(0, Number(data.attendanceHours || data.scheduledNetHours || 0));
            if (data.published === true && isAttendance && data.attendanceAllocateHours === true && data.attendancePaid !== false && hours > 0) {
              attendanceRows.push({ storeId: storeKey, date: dateKey, staffId: staffIdKey, name: String(data.staffName || data.name || 'Staff member'),
                attendanceLabel: String(data.attendanceLabel || 'Paid leave'), attendanceHours: hours, attendancePaid: data.attendancePaid !== false });
            }
            return;
          }


          if (staffIdKey) next.set(`${storeKey}|id:${staffIdKey}|${dateKey}`, publishedShift);
          if (staffNameKey) next.set(`${storeKey}|name:${staffNameKey}|${dateKey}`, publishedShift);
        });
        setPublishedScheduleMap(next);
        setPublishedAttendanceRows(attendanceRows);
        setPublishedForecastEntries(forecastEntries);
        setScheduleEvidenceReady(true);
      },
      (error) => {
        console.warn('Could not load published schedule for Payroll', error);
        setPublishedScheduleMap(new Map());
        setPublishedAttendanceRows([]);
        setPublishedForecastEntries([]);
        setScheduleEvidenceReady(false);
      }
    );
  }, [orgId]);

  /* ---- Readers ---- */
  const readNestedForStore = useCallback(async (storeId) => {
    try {
      const col = collection(db, 'orgs', orgId, 'stores', storeId, 'shifts');
      const snap = await getDocs(col);
      return snap.docs.map((d) => normalizeShiftDoc(d, ['orgs', orgId, 'stores', storeId, 'shifts', d.id], storeId));
    } catch { return []; }
  }, [orgId]);

  const readLegacyAllForOrg = useCallback(async () => {
    try {
      const snap = await getDocs(q(collection(db, 'cashup_shifts'), where('orgId', '==', orgId)));
      return snap.docs.map((d) => normalizeShiftDoc(d, ['cashup_shifts', d.id], d.data()?.storeId || ''));
    } catch { return []; }
  }, [orgId]);

  /* ---- Load & compute ---- */
  const load = useCallback(async () => {
    if (!orgId) return;

    // Historical earnings are never hidden by staff name or by later staff offboarding.
    // Old org/settings/payroll.archivedStaff is intentionally ignored: the former
    // name-only archive suppressed legitimate past wage records in every month.

    let candidates = [];
    const selectedId = store.value;
    const selectedName = storeIdToNameRef.current[selectedId];

    if (selectedId !== 'ALL') {
      const [nested, legacy] = await Promise.all([readNestedForStore(selectedId), readLegacyAllForOrg()]);
      const legacyForStore = legacy.filter((s) => s.store === selectedId || s.store === selectedName);
      const seen = new Set();
      [...nested, ...legacyForStore].forEach((r) => {
        const k = (r.__path || []).join('/') || r.id;
        if (!seen.has(k)) { seen.add(k); candidates.push(r); }
      });
    } else {
      const storeIds = stores.filter((o) => o.value !== 'ALL').map((o) => o.value);
      const nestedLists = await Promise.all(storeIds.map((sid) => readNestedForStore(sid)));
      const legacy = await readLegacyAllForOrg();
      const seen = new Set();
      [...nestedLists.flat(), ...legacy].forEach((r) => {
        const k = (r.__path || []).join('/') || r.id;
        if (!seen.has(k)) { seen.add(k); candidates.push(r); }
      });
    }

    if (!siteScope.allSites) {
      const allowedSiteIds = new Set((siteScope.siteIds || []).map(String));
      candidates = candidates.filter((row) => {
        const rawStore = String(row.storeId || row.store || '');
        const resolvedId = storeNameToIdRef.current[rawStore] || rawStore;
        return allowedSiteIds.has(String(resolvedId));
      });
    }

    const start = new Date(dateFrom); start.setHours(0,0,0,0);
    const end   = new Date(dateTo);   end.setHours(23,59,59,999);
    const filtered = dedupePayrollRows(
      candidates
        .filter((s) => {
          if (s.archived) return false;
          if (s.payrollZeroHours === true || (s.scheduleVarianceRejected === true && s.scheduleVarianceType === "swap_rejected")) return false;
          if (!s.clockIn?.seconds) return false;

          const ci = new Date(s.clockIn.seconds * 1000);
          if (ci < start || ci > end) return false;

          // Payroll consumes concluded shifts only. An approval can classify
          // overtime, but it must never finalize a shift that is still open.
          return !!s.clockOut?.seconds;
        })
        .sort((a,b) => (a.name || '').localeCompare(b.name || '') || (a.clockIn?.seconds || 0) - (b.clockIn?.seconds || 0)),
      getEffectiveOutSec
    );

    setRows(filtered);
    
    // Build per-person summary and date-stamped, in-memory evidence for the forecast.
    const per = new Map();
    const acceptedDays = [];

    for (const s of filtered) {
      const ciSec = toSec(s.clockIn);
      const coSec = getDisplayClockOutSec(s, resolveShiftThresholdHours(s));
      if (typeof ciSec !== 'number' || typeof coSec !== 'number' || coSec <= ciSec) continue;

      const ci = new Date(ciSec * 1000);
      const dateKey = localDateKeyFromSec(ciSec);

      const rawStore = String(s.store || '').trim();
      const storeId =
        storeNameToIdRef.current[rawStore] ||
        storeNameToIdRef.current[rawStore.toLowerCase()] ||
        rawStore;

      const scheduledStaffId =
        s.scheduleVarianceType === 'swap' && s.swapExpectedStaffId
          ? String(s.swapExpectedStaffId)
          : String(s.staffId || '');

      const scheduledStaffName =
        s.scheduleVarianceType === 'swap' && s.swapExpectedStaffName
          ? String(s.swapExpectedStaffName)
          : String(s.staffNameSnapshot || s.name || '');

      const publishedShift =
        (scheduledStaffId
          ? publishedScheduleMap.get(`${storeId}|id:${scheduledStaffId}|${dateKey}`)
          : '') ||
        (scheduledStaffName
          ? publishedScheduleMap.get(`${storeId}|name:${scheduledStaffName.trim().toLowerCase()}|${dateKey}`)
          : '') ||
        '';

      const boundaries = scheduleBoundarySeconds(dateKey, publishedShift);

      let regularSeconds = 0;
      let approvedOvertimeSeconds = 0;

      if (typeof boundaries.start === 'number' && typeof boundaries.end === 'number') {
        const overlapStart = Math.max(ciSec, boundaries.start);
        const overlapEnd = Math.min(coSec, boundaries.end);
        regularSeconds = Math.max(0, overlapEnd - overlapStart);

        const earlyOutside = Math.max(0, Math.min(coSec, boundaries.start) - ciSec);
        const lateOutside = Math.max(0, coSec - Math.max(ciSec, boundaries.end));
        const outsideSeconds = earlyOutside + lateOutside;

        // Pending outside-schedule time is deliberately excluded from Payroll.
        if (s.scheduleVarianceApproved === true) {
          approvedOvertimeSeconds = outsideSeconds;
        }
      } else if (s.scheduleVarianceApproved === true) {
        // Unscheduled/replacement work enters Payroll only after explicit approval.
        approvedOvertimeSeconds = Math.max(0, coSec - ciSec);
      }

      let regularH = round2(regularSeconds / 3600);
      let approvedOtH = round2(approvedOvertimeSeconds / 3600);

      // Lunch remains a deduction from accepted payable time, as before.
      const lmRaw = typeof s.lunchMin === 'number'
        ? s.lunchMin
        : (typeof s.lunchMin === 'string' ? Number(s.lunchMin) : NaN);
      const lm = Number.isFinite(lmRaw) ? lmRaw : (typeof lunchMin === 'number' ? lunchMin : 0);
      let lunchRemaining = round2(Math.max(0, lm || 0) / 60);

      // Deduct lunch from regular scheduled time first; only spill into approved OT
      // if the accepted regular period is shorter than the configured break.
      const regularLunch = Math.min(regularH, lunchRemaining);
      regularH = round2(Math.max(0, regularH - regularLunch));
      lunchRemaining = round2(Math.max(0, lunchRemaining - regularLunch));
      if (lunchRemaining > 0) {
        approvedOtH = round2(Math.max(0, approvedOtH - lunchRemaining));
      }

      const paidH = round2(regularH + approvedOtH);
      if (paidH <= 0) continue;

      const name = s.name || '(unknown)';
      const canonicalStaffId = String(s.staffId || '').trim();
      const key = canonicalStaffId ? `${storeId}__${canonicalStaffId}` : `UNLINKED_${storeId}_${staffKey(name)}`;
      if (!per.has(key)) {
        per.set(key, {
          key,
          name, staffId: canonicalStaffId, storeId,
          monSatHrs: 0,
          publicHrs: 0,
          sundayRaw: 0,
          approvedOvertimeHrs: 0,
          reviewReasons: [],
        });
      }

      const row = per.get(key);
      // PR2: a shift crossing midnight cannot safely be classified solely by
      // clock-in day. Keep its draft calculation visible but flag for review.
      if (localDateKeyFromSec(coSec) !== dateKey && !row.reviewReasons.includes('Overnight shift: verify day/premium allocation')) row.reviewReasons.push('Overnight shift: verify day/premium allocation');
      if (publicPayEnabled && publicHolidays.has(dateKey) && ci.getDay() === 0 && !row.reviewReasons.includes('Sunday public holiday: review premium treatment')) row.reviewReasons.push('Sunday public holiday: review premium treatment');
      if (approvedOtH>0 && (ci.getDay()===0||(publicPayEnabled&&publicHolidays.has(dateKey))) && !row.reviewReasons.includes('Premium overlap: approved overtime on Sunday/holiday')) row.reviewReasons.push('Premium overlap: approved overtime on Sunday/holiday');
      const isPublic = publicPayEnabled && publicHolidays.has(dateKey) && ci.getDay() !== 0;

      // Approved overtime remains overtime regardless of Sunday/public-holiday
      // classification. Regular accepted hours go into the calendar bucket.
      row.approvedOvertimeHrs += approvedOtH;

      if (ci.getDay() === 0) row.sundayRaw += regularH;
      else if (isPublic) row.publicHrs += regularH;
      else row.monSatHrs += regularH;
      const evidenceFlags = [];
      if (localDateKeyFromSec(coSec)!==dateKey) evidenceFlags.push('Overnight shift: verify day/premium allocation');
      if (publicPayEnabled && publicHolidays.has(dateKey) && ci.getDay()===0) evidenceFlags.push('Sunday public holiday: review premium treatment');
      if (approvedOtH>0 && (ci.getDay()===0 || (publicPayEnabled&&publicHolidays.has(dateKey)))) evidenceFlags.push('Premium overlap: approved overtime on Sunday/holiday');
      acceptedDays.push({ key, name, staffId: canonicalStaffId, storeId, date:dateKey,
        monSatHrs:ci.getDay()===0||isPublic?0:regularH, sundayHours:ci.getDay()===0?regularH:0,
        publicHours:isPublic?regularH:0, overtimeHrs:approvedOtH, paidLeaveHrs:0,
        reviewReasons: evidenceFlags, sourcePath:(s.__path||[]).join('/') });
    }

    // Publish-approved leave in the current month can extend beyond today.
    // Upcoming dates are forecast leave, not money already earned. Only leave
    // up to TODAY IN EMPLOYER TIMEZONE enters the accrued earnings bucket.
    const earnedThrough = payrollDayInZone(new Date(), payrollTimeZone);
    publishedAttendanceRows.forEach((leave) => {
      if (selectedId !== 'ALL' && String(leave.storeId) !== String(selectedId)) return;
      const leaveDate = new Date(`${leave.date}T12:00:00`);
      if (Number.isNaN(leaveDate.getTime()) || leaveDate < start || leaveDate > end) return;
      const name = leave.name || '(unknown)';
      const staffId = String(leave.staffId || '').trim();
      const storeId = String(leave.storeId || '').trim();
      const key = staffId ? `${storeId}__${staffId}` : `UNLINKED_${storeId}_${staffKey(name)}`;
      if (!per.has(key)) per.set(key, { key, name, staffId, storeId, monSatHrs: 0, publicHrs: 0, sundayRaw: 0, approvedOvertimeHrs: 0, paidLeaveHrs: 0, scheduledFutureLeaveHrs: 0, reviewReasons: [] });
      const row = per.get(key);
      const hours = round2(Math.max(0, Number(leave.attendanceHours) || 0));
      if (String(leave.date) > earnedThrough) {
        row.scheduledFutureLeaveHrs = round2((row.scheduledFutureLeaveHrs || 0) + hours);
        // forEach callback: skip this leave entry, not the surrounding payroll calculation.
        return;
      }
      // Paid leave is its own exclusive bucket, not duplicated in worked hours.
      row.paidLeaveHrs = round2((row.paidLeaveHrs || 0) + hours);
      acceptedDays.push({key,name,staffId,storeId,date:String(leave.date),
        monSatHrs:0,sundayHours:0,publicHours:0,overtimeHrs:0,paidLeaveHrs:hours,reviewReasons:[]});
    });

    const out = Array.from(per.values()).map((r) => {
      const sundayHours = r.sundayRaw;
      const publicHours = r.publicHrs || 0;
      const overtimeHrs = r.approvedOvertimeHrs || 0;
      const paidLeaveHrs = r.paidLeaveHrs || 0;
      const totalHrs = r.monSatHrs + publicHours + sundayHours + overtimeHrs + paidLeaveHrs;

      return {
        key: r.key,
        name: r.name, staffId: r.staffId || '', storeId: r.storeId || '',
        monSatHrs: +r.monSatHrs.toFixed(2),
        publicHours: +publicHours.toFixed(2),
        sundayHours: +sundayHours.toFixed(2),
        sundayRaw: +r.sundayRaw.toFixed(2),
        overtimeHrs: +overtimeHrs.toFixed(2),
        paidLeaveHrs: +paidLeaveHrs.toFixed(2),
        scheduledFutureLeaveHrs: +(r.scheduledFutureLeaveHrs||0).toFixed(2),
        totalHrs: +totalHrs.toFixed(2),
        reviewReasons: r.reviewReasons || [],
      };
    }).sort((a,b) => a.name.localeCompare(b.name));

    setSummary(out);
    setAcceptedPayrollDays(acceptedDays);
  }, [orgId, store, stores, dateFrom, dateTo, lunchMin, publicPayEnabled, publicHolidays, readNestedForStore, readLegacyAllForOrg, resolveShiftThresholdHours, getEffectiveOutSec, publishedScheduleMap, publishedAttendanceRows, payrollTimeZone]);

  // Calculations stay in memory, behind payroll's fresh-authenticated unlock.
  // A draft is NOT a compliant payslip, final payroll or statutory net pay.
  const financialDataReady = payrollUnlocked && periodIsCurrent && payrollDataScope===selectedFingerprint && store?.value!=='ALL';
  const calculatedRows = useMemo(()=>(financialDataReady?summary:[]).map(row=>{
    const profile=profilesByKey[`${row.storeId}__${row.staffId}`];
    const payBasis=profile?.payBasis||'reference-hours';
    const refHours=Number(profile?.referenceHours)>0?Number(profile.referenceHours):totalHours;
    const pay=calculateDraftEarnings(row, salary[row.key], refHours, earningRules, draftItems[row.key]||[], adj[row.key]||0, payBasis);
    return {...row,pay,items:draftItems[row.key]||[]};
  }),[summary,financialDataReady,profilesByKey,totalHours,salary,earningRules,draftItems,adj]);
  const calculatedByKey = useMemo(()=>Object.fromEntries(calculatedRows.map(r=>[r.key,r])),[calculatedRows]);
  const financialComplete = financialDataReady && store?.value!=='ALL' && calculatedRows.length>0 && calculatedRows.every(r=>r.pay.ready);
  const draftTotals = financialComplete ? calculatedRows.reduce((acc,r)=>{
    acc.gross+=r.pay.grossBeforeStatutory;acc.deductions+=r.pay.deductions;acc.incomplete+=Number(r.pay.hasUnclassifiedAdjustment||(r.reviewReasons||[]).length>0);return acc;
  },{gross:0,deductions:0,incomplete:0}):null;
  const openItemModal=(row)=>{
    setItemError('');
    setItemModal({open:true,row,items:(draftItems[row.key]||[]).map(item=>({...item}))});
  };
  const addItem=()=>setItemModal(m=>({...m,items:[...m.items,{
    id:`item_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,kind:'earning',category:'allowance',amount:'',description:'',authorizationReference:'',taxTreatment:'pending',taxEvidenceReference:''
  }]}));
  const editItem=(idx,changes)=>setItemModal(m=>({...m,items:m.items.map((item,i)=>i===idx?{...item,...changes}:item)}));
  const removeItem=(idx)=>setItemModal(m=>({...m,items:m.items.filter((_,i)=>i!==idx)}));
  const saveItems=async()=>{
    if(!saveItemisedRef.current||!itemModal.row)return;
    setItemSaving(true);setItemError('');
    try {
      await saveItemisedRef.current(itemModal.row,itemModal.items);
      setItemModal({open:false,row:null,items:[]});
    }catch(e){setItemError(String(e?.message||e).replace(/^FirebaseError:\s*/,''));}
    finally{setItemSaving(false);}
  };

  /* =================== Exports =================== */
  const doPdf = () => {
    if(!payrollUnlocked||!periodIsCurrent){toast.warning('Unlock payroll and reload the selected period before exporting.');return;}
    const doc = new jsPDF('l', 'pt', 'letter');

    const fromStr = dateFrom ? dateFrom.toLocaleDateString() : '—';
    const toStr   = dateTo   ? dateTo.toLocaleDateString()   : '—';
    const header = `${orgName || ""}${store?.label ? ` — ${store.label}` : ""}`.trim();
    // Slightly lower the header/meta block for cleaner spacing under the title
    if (header) {
      doc.setFontSize(11);
      doc.text(header, 40, 66);
    }

    const meta = `Period: ${fromStr} — ${toStr}   •   Total hours: ${totalHours}`;

    doc.setFontSize(18);
    doc.text('Payroll · DRAFT', 40, 40);
    doc.setFontSize(11);
    doc.setTextColor('#6b7280');
    // Drop period line by one “line” for clearer spacing
    doc.text(meta, 40, 88);
    doc.setTextColor('#000');

    const head = [[
      'Name',
      'Hours (Mon–Sat)',
      'Sunday Hours',
      'Public Hours',
      'Overtime (hrs)',
      'Paid Leave (hrs)',
      'Total (hrs)',
      'Hourly Rate (R)',
      'Basic Salary (R)',
      'Adj (+/– R)',
      'Note',
    ]];

    const body = summary.map((r) => {
      const k     = r.key;
      const basic = asMoney(salary?.[k] ?? 0);
      const rate  = totalHours ? asMoney(basic / totalHours) : 0;
      const adjR  = asMoney(adj?.[k] ?? 0);
      const memo  = (note?.[k] ?? '') || '';
      return [
        r.name,
        num(r.monSatHrs),
        num(r.sundayHours),
        num(r.publicHours),
        num(r.overtimeHrs),
        num(r.paidLeaveHrs),
        num(r.totalHrs),
        num(rate),
        num(basic),
        num(adjR),
        memo,
      ];
    });

    autoTable(doc, {
      head, body, startY: 98,
      headStyles: { fillColor: '#334155', textColor: '#ffffff' },
      styles: { fontSize: 10, cellPadding: 6 },
      // Make the notes column narrower so everything fits better
      columnStyles: { 9: { cellWidth: 120 } },
    });

    const safe = (s) => String(s || "").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    doc.addPage();
    doc.setTextColor(20,40,60);doc.setFontSize(16);doc.text('PR2 · Draft earnings — NOT a payment instruction',40,42);
    doc.setFontSize(9);doc.text('PAYE/UIF, statutory deductions and payroll finalisation are not included. Unapproved hours are excluded.',40,60);
    autoTable(doc, {
      startY:76,
      head:[['Employee','Site','Ordinary','Sunday','Holiday','OT','Leave','Extras','Gross','Deductions','Review']],
      body:calculatedRows.map(row=>{
        const x=row.pay;return [row.name,stores.find(st=>String(st.value)===String(row.storeId))?.label||row.storeId,
          x.ready?num(x.ordinary):'—',x.ready?num(x.sunday):'—',x.ready?num(x.holiday):'—',
          x.ready?num(x.overtime):'—',x.ready?num(x.leave):'—',x.ready?num(x.earningsItems):'—',
          x.ready?num(x.grossBeforeStatutory):'—',x.ready?num(x.deductions):'—',
          !x.ready?x.reason:[...(row.reviewReasons||[]),...(x.hasUnclassifiedAdjustment?['Unclassified adjustment excluded']:[])].join('; ')];
      }),
      styles:{fontSize:8,cellPadding:4},headStyles:{fillColor:[26,55,77]},
    });
    doc.save(`payroll_DRAFT_${safe(header) || "summary"}.pdf`);
  };

  const doExcel = () => {
    if(!payrollUnlocked||!periodIsCurrent){toast.warning('Unlock payroll and reload the selected period before exporting.');return;}
    const wb = XLSX.utils.book_new();

    const metaWs = XLSX.utils.aoa_to_sheet([
      ['Payroll'],
      ['Period', (dateFrom ? dateFrom.toLocaleDateString() : '—'), 'to', (dateTo ? dateTo.toLocaleDateString() : '—')],
      ['Total hours', totalHours],
    ]);
    XLSX.utils.book_append_sheet(wb, metaWs, 'Meta');

    const out = [
      ['Name', 'Hours (Mon–Sat)', 'Sunday Hours', 'Public Hours', 'Overtime (hrs)', 'Paid Leave (hrs)', 'Total (hrs)', 'Hourly Rate (R)', 'Basic Salary (R)', 'Adj (+/– R)', 'Note'],
      ...summary.map((r) => {
        const k     = r.key;
        const basic = asMoney(salary?.[k] ?? 0);
        const rate  = totalHours ? asMoney(basic / totalHours) : 0;
        const adjR  = asMoney(adj?.[k] ?? 0);
        const memo  = (note?.[k] ?? '') || '';
        return [
          r.name,
          +num(r.monSatHrs),
          +num(r.sundayHours),
          +num(r.publicHours),
          +num(r.overtimeHrs),
          +num(r.paidLeaveHrs),
          +num(r.totalHrs),
          +num(rate),
          +num(basic),
          +num(adjR),
          memo,
        ];
      }),
    ];
    const sumWs = XLSX.utils.aoa_to_sheet(out);
    XLSX.utils.book_append_sheet(wb, sumWs, 'Summary');

    const moneyRows=[['PR2 DRAFT · NOT FOR PAYMENT · statutory PAYE/UIF not calculated'],
      ['Employee','Site','Ordinary pay','Sunday pay','Holiday pay','Approved OT pay','Paid leave pay','Other earnings','Gross before statutory','Authorised draft deductions','After deductions BEFORE tax','Legacy adj excluded','Review / missing profile']];
    calculatedRows.forEach(row=>{
      const pay=row.pay;
      moneyRows.push([row.name,stores.find(st=>String(st.value)===String(row.storeId))?.label||row.storeId,
        pay.ready?pay.ordinary:'',pay.ready?pay.sunday:'',pay.ready?pay.holiday:'',pay.ready?pay.overtime:'',
        pay.ready?pay.leave:'',pay.ready?pay.earningsItems:'',pay.ready?pay.grossBeforeStatutory:'',
        pay.ready?pay.deductions:'',pay.ready?pay.afterNonStatutoryDeductions:'',pay.legacyAdjustment||'',
        pay.ready?[...(row.reviewReasons||[]),...(pay.hasUnclassifiedAdjustment?['Unclassified adjustment excluded']:[])].join('; '):pay.reason]);
    });
    XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(moneyRows),'Draft Earnings');
    const itemRows=[['PR2 DRAFT · Employer-supplied deductions require lawful authority'],
      ['Employee','Site','Kind','Category','Description','Amount','Authorisation reference']];
    calculatedRows.forEach(row=>row.items.forEach(item=>itemRows.push([row.name,row.storeId,item.kind,item.category,item.description,item.amount,item.authorizationReference||''])));
    XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet(itemRows),'Draft Line Items');
    XLSX.writeFile(wb, 'payroll-PR2-DRAFT-not-for-payment.xlsx');
  };

  const csvData = useMemo(() => {
    const fromStr = dateFrom ? dateFrom.toLocaleDateString() : '';
    const toStr   = dateTo   ? dateTo.toLocaleDateString()   : '';
    // Each row uses the same keys; do not use a mismatched header-only object.
    const headerLine = [];

    const lines = summary.map((r) => {
      const k     = r.key;
      const basic = asMoney(salary?.[k] ?? 0);
      const rate  = totalHours ? asMoney(basic / totalHours) : 0;
      const adjR  = asMoney(adj?.[k] ?? 0);
      const memo  = (note?.[k] ?? '') || '';
      const computed=calculatedByKey[r.key]?.pay;
      return {
        Draft_Status:'NOT FINAL - PAYE UIF NOT CALCULATED',
        Period:`${fromStr} — ${toStr}`,
        Name: r.name,
        Hours_MonSat: +num(r.monSatHrs),
        Sunday_Hours: +num(r.sundayHours),
        Public_Hours: +num(r.publicHours),
        Overtime_hrs: +num(r.overtimeHrs),
        PaidLeave_hrs: +num(r.paidLeaveHrs),
        Total_hrs: +num(r.totalHrs),
        HourlyRate_R: +num(rate),
        BasicSalary_R: +num(basic),
        Adj_R: +num(adjR),
        Note: memo,
        Draft_OrdinaryPay:computed?.ready?computed.ordinary:'',
        Draft_SundayPay:computed?.ready?computed.sunday:'',
        Draft_PublicPay:computed?.ready?computed.holiday:'',
        Draft_ApprovedOvertimePay:computed?.ready?computed.overtime:'',
        Draft_PaidLeavePay:computed?.ready?computed.leave:'',
        Draft_ExtraEarnings:computed?.ready?computed.earningsItems:'',
        Draft_GrossBeforeTax:computed?.ready?computed.grossBeforeStatutory:'',
        Draft_ListedDeductions:computed?.ready?computed.deductions:'',
        Warning:computed?.ready?((computed.hasUnclassifiedAdjustment?'Legacy adjustment excluded; ':'')+(r.reviewReasons||[]).join('; ')):computed?.reason||'No salary',
      };
    });

    return [...headerLine, ...lines];
  }, [summary, dateFrom, dateTo, totalHours, salary, adj, note, calculatedByKey]);


  const openPayrollDetails = (row) => {
    if (!row?.key) return;
    setPayrollDetailModal({
      open: true,
      key: row.key,
      name: row.name || 'Staff member',
      adjustment: adj?.[row.key] ?? '',
      note: note?.[row.key] ?? '',
    });
  };

  const savePayrollDetails = async () => {
    const k = payrollDetailModal.key;
    const row = summary.find(x=>x.key===k);
    if (!k || !row) return;
    if (!payrollUnlocked || !payrollCanEdit || !saveDraftRef.current){toast.error('Unlock Payroll with editing permission and select a specific site.');return;}
    try {
      await saveDraftRef.current(row,payrollDetailModal.adjustment,payrollDetailModal.note);
      setAdj((m) => ({ ...m, [k]: payrollDetailModal.adjustment }));
      setNote((m) => ({ ...m, [k]: payrollDetailModal.note }));
      setPayrollDetailModal({ open: false, key: '', name: '', adjustment: '', note: '' });
      toast.success('Saved payroll draft adjustment securely.');
    } catch(error){toast.error(error?.message||'Could not save payroll adjustment.');}
  };

  /* =================== PR1.1 full-width Payroll workspace =================== */
  // PR3.6A: read-only forecast; existing payroll/financial security stays untouched.
  const periodStartKey = localDay(dateFrom);
  const periodEndKey = localDay(dateTo);
  const yesterdayInEmployerZone = useMemo(() => {
    const today = payrollDayInZone(new Date(), payrollTimeZone);
    return new Date(Date.parse(`${today}T12:00:00Z`) - 86400000).toISOString().slice(0,10);
  }, [payrollTimeZone]);
  const latestActualDate = periodEndKey < yesterdayInEmployerZone ? periodEndKey : yesterdayInEmployerZone;
  const effectiveForecastCutoff = forecastCutoff || latestActualDate;
  const forecast = useMemo(() => {
    if (!periodIsCurrent || !scheduleEvidenceReady) return null;
    if (effectiveForecastCutoff > latestActualDate) return { valid:false, reason:'Actual-through date cannot be today or a future date. Select a completed day.' };
    return buildPayrollForecast({
      acceptedDays:acceptedPayrollDays, publishedEntries:publishedForecastEntries,
      periodStart:periodStartKey, periodEnd:periodEndKey, cutoff:effectiveForecastCutoff,
      selectedStoreId:String(store?.value||'ALL'), allowedStoreIds:stores.filter(x=>x.value!=='ALL').map(x=>String(x.value)),
      publicHolidays, publicPayEnabled, defaultLunchMin:lunchMin,
    });
  }, [periodIsCurrent,scheduleEvidenceReady,acceptedPayrollDays,publishedForecastEntries,
      periodStartKey,periodEndKey,effectiveForecastCutoff,latestActualDate,store,stores,publicHolidays,publicPayEnabled,lunchMin]);
  const payrollTabs = ['Overview','Forecast','Readiness','Exceptions','Reconciliation','Carry-forwards','Earnings','Deductions','Payslips','Settings','History'];
  const shownSummary = (periodIsCurrent?summary:[]).filter(r=>`${r.name||''} ${r.role||''}`.toLowerCase().includes(employeeSearch.trim().toLowerCase()));
  const metrics = (periodIsCurrent?summary:[]).reduce((acc,r)=>{
    acc.hours += Number(r.totalHrs)||0;
    acc.overtime += Number(r.overtimeHrs)||0;
    acc.leave += Number(r.paidLeaveHrs)||0;
    return acc;
  },{hours:0,overtime:0,leave:0});
  const refreshPayroll=async()=>{
    setExceptionReviewKey(null);
    setPeriodLoading(true);setPeriodMessage('Loading the selected payroll period…');
    try{setLoadedFingerprint('');await load();setLoadedFingerprint(selectedFingerprint);setPeriodMessage('Attendance period loaded. PR2.4 overtime decisions are auditable drafts; PAYE/UIF and final approval are not complete.');}
    catch(e){setPeriodMessage(`Could not load payroll period: ${e?.message||String(e)}`);}
    finally{setPeriodLoading(false);}
  };
  const money=(value)=>new Intl.NumberFormat('en-ZA',{minimumFractionDigits:2,maximumFractionDigits:2}).format(Number(value)||0);
  // Display the active saved draft multipliers without altering calculation logic.
  const factor=(value)=>Number.isFinite(Number(value))?Number(value).toFixed(2):'—';
  const payHeading=(title,detail)=> <span className="block whitespace-nowrap">{title}{payrollUnlocked&&<span className="block text-[10px] font-medium text-cyan-200">{detail}</span>}</span>;
  const contractualExcess=(row)=>{
    const profile=profilesByKey[`${row.storeId}__${row.staffId}`];
    const monthlyReference=Number(profile?.referenceHours)||Number(totalHours)||180;
    const nonOvertimePaid=Number(row.monSatHrs||0)+Number(row.sundayHours||0)+Number(row.publicHours||0)+Number(row.paidLeaveHrs||0);
    return Math.max(0,Math.round((nonOvertimePaid-monthlyReference)*100)/100);
  };
  const getContractualMetrics=(row)=>{
    const profile=profilesByKey[`${row.storeId}__${row.staffId}`];
    const referenceHours=Math.round((Number(profile?.referenceHours)||Number(totalHours)||180)*100)/100;
    return {
      ordinaryHours:Number(row.monSatHrs)||0,sundayHours:Number(row.sundayHours)||0,
      holidayHours:Number(row.publicHours)||0,paidLeaveHours:Number(row.paidLeaveHrs)||0,
      alreadyApprovedOvertimeHours:Number(row.overtimeHrs)||0,referenceHours,
      excessHours:contractualExcess(row)
    };
  };
  const isCurrentContractualReview=(row,review)=>{
    if(!review)return false;
    const metrics=getContractualMetrics(row);
    return Object.keys(metrics).every(k=>Math.abs(Number(metrics[k])-Number(review[k]))<0.011);
  };
  const saveExcessReview=async(row)=>{
    if(!payrollUnlocked||!payrollCanEdit||!financialDataReady||!saveContractualReviewRef.current){
      setContractualReviewMessage('Unlock Payroll with editing permission and load one site and payroll period.');return;
    }
    const metrics=getContractualMetrics(row);
    if(!row.staffId||metrics.excessHours<=0){setContractualReviewMessage('No linked employee or current contractual excess to review.');return;}
    if(contractualReviewInput.reason.trim().length<15){setContractualReviewMessage('Add a reason of at least 15 characters based on the attendance evidence.');return;}
    setContractualReviewBusy(true);setContractualReviewMessage('');
    try {
      await saveContractualReviewRef.current(row,metrics,contractualReviewInput.status,contractualReviewInput.reason.trim());
      setContractualReviewMessage('Decision recorded securely. No automatic premium has been added; exact shift allocation remains required.');
    } catch(e){setContractualReviewMessage(e?.message||'Could not save contractual review.');}
    finally{setContractualReviewBusy(false);}
  };
  const panel='rounded-2xl border border-cyan-400/20 bg-[#0d223d] p-4 md:p-5';
  const navButton=(name)=><button key={name} type="button" onClick={()=>setActivePayrollTab(name)} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition ${activePayrollTab===name?'bg-cyan-600 text-white':'border border-white/15 bg-[#07172b] text-slate-300 hover:text-white hover:border-cyan-300/50'}`} aria-current={activePayrollTab===name?'page':undefined}>{name}</button>;

  return (
    <div data-neo-payroll-workspace="true" className="w-full min-h-full text-white bg-[#071321]">
      <div className="mx-auto w-full max-w-[1920px] px-3 py-4 md:px-6 md:py-6 space-y-4">
        <div className={`${panel} flex flex-wrap items-start justify-between gap-4`}>
          <div><button type="button" onClick={()=>navigate(backPath)} className="mb-3 rounded-lg border border-white/15 px-3 py-2 text-xs text-cyan-200 hover:bg-white/5">← Back to Neo Admin</button><div className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">Neo Admin · Workforce Finance</div><h1 className="mt-1 text-3xl font-black">Payroll Command Center</h1><p className="mt-1 text-sm text-slate-300">Review concluded hours, manage secure salaries and prepare payroll by period and site.</p></div>
          <div className="flex flex-col items-end gap-2"><span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-100">Draft only · Not finalised</span><span className="text-xs text-slate-400">{payrollUnlocked?'Financial access unlocked':'Financial information locked'}</span></div>
        </div>
        <div className="flex max-w-full gap-2 overflow-x-auto pb-2" role="tablist" aria-label="Payroll sections">{payrollTabs.map(navButton)}</div>
        <div className={panel}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 items-end">
            <label className="text-xs text-slate-300">Period start<DatePicker selected={dateFrom} onChange={(d)=>{if(d){d.setHours(0,0,0,0);setDateFrom(d);setForecastCutoff('');}}} className="mt-1 w-full rounded-lg border border-white/15 bg-[#07172b] px-3 py-2 text-sm text-white"/></label>
            <label className="text-xs text-slate-300">Period end<DatePicker selected={dateTo} onChange={(d)=>{if(d){d.setHours(23,59,59,999);setDateTo(d);setForecastCutoff('');}}} className="mt-1 w-full rounded-lg border border-white/15 bg-[#07172b] px-3 py-2 text-sm text-white"/></label>
            <label className="text-xs text-slate-300 xl:col-span-2">Site<div className="mt-1"><Select options={stores} value={store} onChange={setStore} styles={selectStyles} theme={selectTheme}/></div></label>
            <button type="button" onClick={refreshPayroll} disabled={!orgId||periodLoading} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-bold hover:bg-cyan-500 disabled:opacity-50">{periodLoading?'Loading…':'Load payroll period'}</button>
          </div>
          {periodMessage&&<div role="status" aria-live="polite" className="mt-3 rounded-lg bg-white/[.04] px-3 py-2 text-xs text-slate-300">{periodMessage}</div>}
          {!periodIsCurrent&&summary.length>0&&<div role="status" className="mt-2 rounded-lg bg-amber-500/10 p-3 text-xs text-amber-100">Filters changed. Reload the payroll period before reviewing amounts or exporting.</div>}
        </div>
        {/* Leave this mounted even when another sub-tab is selected; switching tabs must
            not destroy the fresh-authentication session or unsaved profile inputs. */}
        <div style={{display:activePayrollTab==='Settings'?'block':'none'}} aria-hidden={activePayrollTab!=='Settings'}>
          <PayrollFoundationPanel orgId={orgId} summary={securePayrollSummary} from={dateFrom} to={dateTo} site={store} siteOptions={stores} shadowRows={calculatedRows} shadowAcceptedDays={acceptedPayrollDays} shadowPublishedEntries={publishedForecastEntries} onFinancialState={onFinancialState}/>
        </div>
        {activePayrollTab!=='Settings'&&!payrollUnlocked&&<div className="rounded-xl border border-amber-300/25 bg-amber-400/10 p-3 text-sm text-amber-100">Financial amounts are locked. Open <button type="button" className="font-semibold underline" onClick={()=>setActivePayrollTab('Settings')}>Settings → Unlock payroll</button> to verify your Neo Admin account.</div>}
        {!orgId&&<div className="rounded-xl border border-rose-400/25 bg-rose-500/10 p-3 text-sm">No organisation ID found. Contact your Neo Admin administrator.</div>}
        {activePayrollTab==='Overview'&&<>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[["Employees in loaded period",String(summary.length)],["Payable hours",metrics.hours.toFixed(2)+'h'],["Approved overtime",metrics.overtime.toFixed(2)+'h'],["Paid leave",metrics.leave.toFixed(2)+'h']].map(([label,value])=><div key={label} className={panel}><div className="text-xs text-slate-400">{label}</div><div className="mt-2 text-2xl font-bold">{value}</div></div>)}
          </div>
          <div className={panel}><div className="flex flex-wrap justify-between gap-3"><div><h2 className="text-lg font-bold">Draft earnings overview</h2><p className="mt-1 text-sm text-slate-400">Payable attendance × employer-configured earning multipliers; not final or statutory payroll.</p></div><span className="h-fit rounded-lg border border-white/15 px-3 py-1 text-xs text-slate-300">Payroll Intelligence · Later phase</span></div>
            {draftTotals?<div className="mt-4 grid gap-3 sm:grid-cols-3">{[["Draft gross before PAYE/UIF",draftTotals.gross],["Itemised non-statutory deductions",draftTotals.deductions],["After listed deductions · before tax",draftTotals.gross-draftTotals.deductions]].map(([label,value])=><div key={label} className="rounded-xl border border-white/10 bg-[#07172b] p-4"><div className="text-xs text-slate-300">{label}</div><div className="mt-2 text-xl font-bold">{payrollCurrency} {money(value)}</div></div>)}</div>:<div className="mt-4 rounded-xl border border-white/10 bg-[#07172b] px-4 py-5 text-sm text-slate-300">Choose one site, load the payroll period, then unlock all linked salary profiles in Settings to view draft figures. All-stores deductions are not yet consolidated.</div>}
            {draftTotals&&<p className="mt-3 text-xs text-amber-200">{draftTotals.incomplete?`${draftTotals.incomplete} employee record(s) require review. `:''}Future scheduled leave is excluded from earned-to-date amounts. PAYE, UIF, other legal withholdings and payroll finalisation are NOT calculated. These figures are not net salary or a payment instruction.</p>}
          </div>
          <div className={panel}><h2 className="text-lg font-bold">Payroll preparation status</h2><p className="mt-2 text-sm text-slate-300">Load attendance for the selected period, unlock financial profiles in Settings, review earnings and draft deductions. Finalisation, approved salary schedules and payslips arrive in later phases.</p><div className="mt-3 flex flex-wrap gap-2"><button className="rounded-lg bg-cyan-600 px-4 py-2 text-sm" onClick={()=>setActivePayrollTab('Earnings')}>Review earnings →</button><button className="rounded-lg border border-white/20 px-4 py-2 text-sm" onClick={()=>setActivePayrollTab('Settings')}>Payroll settings →</button></div></div>
        </>}
        {activePayrollTab==='Forecast'&&<PayrollForecastPanel
          periodStart={periodStartKey} periodEnd={periodEndKey} effectiveCutoff={effectiveForecastCutoff}
          latestActualDate={latestActualDate} onCutoffChange={setForecastCutoff}
          forecast={forecast} current={periodIsCurrent}
          scheduleReady={scheduleEvidenceReady} unlocked={payrollUnlocked} financialReady={financialDataReady}
          currency={payrollCurrency} storeLabels={Object.fromEntries(stores.filter(x=>x.value!=='ALL').map(x=>[String(x.value),x.label]))}
          salaries={salary} profilesByKey={profilesByKey} defaultReferenceHours={totalHours}
          earningRules={earningRules} draftItems={draftItems} legacyAdjustments={adj}
          selectedStoreId={String(store?.value||'ALL')} canEdit={payrollCanEdit} isOwner={payrollIsOwner}
          previewSnapshot={(payload)=>previewForecastSnapshotRef.current
            ? previewForecastSnapshotRef.current(payload)
            : Promise.reject(new Error('Payroll is locked. Unlock again in Settings.'))}
          lockSnapshot={(payload)=>lockForecastSnapshotRef.current
            ? lockForecastSnapshotRef.current(payload)
            : Promise.reject(new Error('Payroll is locked. Unlock again in Settings.'))}
          getSnapshot={(payload)=>getForecastSnapshotRef.current
            ? getForecastSnapshotRef.current(payload)
            : Promise.reject(new Error('Payroll is locked. Unlock again in Settings.'))}
          previewAmendment={(payload)=>previewForecastAmendmentRef.current
            ? previewForecastAmendmentRef.current(payload)
            : Promise.reject(new Error('Payroll is locked. Unlock again in Settings.'))}
          commitAmendment={(payload)=>commitForecastAmendmentRef.current
            ? commitForecastAmendmentRef.current(payload)
            : Promise.reject(new Error('Payroll is locked. Unlock again in Settings.'))} />}
        {activePayrollTab==='Readiness'&&<PayrollReadinessPanel
          siteId={store?.value || 'ALL'} from={periodStartKey} to={periodEndKey}
          unlocked={payrollUnlocked} periodLoaded={periodIsCurrent}
          getReadiness={(payload)=>getFinalPayrollReadinessRef.current
            ? getFinalPayrollReadinessRef.current(payload)
            : Promise.reject(new Error('Unlock payroll in Settings before checking readiness.'))}
        />}
        {activePayrollTab==='Exceptions'&&<PayrollClassificationPreparationPanel
          siteId={String(store?.value||'ALL')} from={periodStartKey} to={periodEndKey}
          unlocked={payrollUnlocked} loaded={periodIsCurrent} canEdit={payrollCanEdit} isOwner={payrollIsOwner} canDelegate={payrollCanDelegate}
          getReadiness={payload=>getFinalPayrollReadinessRef.current?getFinalPayrollReadinessRef.current(payload):Promise.reject(new Error('Unlock Payroll in Settings.'))}
          listCases={payload=>listFinancialClassificationCasesRef.current?listFinancialClassificationCasesRef.current(payload):Promise.reject(new Error('Unlock Payroll in Settings.'))}
          prepareCase={payload=>prepareFinancialClassificationCaseRef.current?prepareFinancialClassificationCaseRef.current(payload):Promise.reject(new Error('Unlock Payroll with editing permission.'))}
          escalateCase={payload=>escalateFinancialClassificationCaseRef.current?escalateFinancialClassificationCaseRef.current(payload):Promise.reject(new Error('Unlock Payroll with editing permission.'))}
          listSources={payload=>listFinancialClassificationSourcesRef.current?listFinancialClassificationSourcesRef.current(payload):Promise.reject(new Error('Unlock Payroll.'))}
          previewSource={payload=>previewFinancialClassificationSourceRef.current?previewFinancialClassificationSourceRef.current(payload):Promise.reject(new Error('Unlock Payroll.'))}
          submitAssessment={payload=>submitFinancialClassificationAssessmentRef.current?submitFinancialClassificationAssessmentRef.current(payload):Promise.reject(new Error('Payroll editing permission required.'))}
          reviewAssessment={payload=>reviewFinancialClassificationAssessmentRef.current?reviewFinancialClassificationAssessmentRef.current(payload):Promise.reject(new Error('Independent Owner review required.'))}
          listReviewers={payload=>listIndependentPayrollReviewersRef.current?listIndependentPayrollReviewersRef.current(payload):Promise.reject(new Error('Owner reviewer management unavailable.'))}
          grantReviewer={payload=>grantIndependentPayrollReviewerRef.current?grantIndependentPayrollReviewerRef.current(payload):Promise.reject(new Error('Owner reviewer management unavailable.'))}
          revokeReviewer={payload=>revokeIndependentPayrollReviewerRef.current?revokeIndependentPayrollReviewerRef.current(payload):Promise.reject(new Error('Owner reviewer management unavailable.'))}
          onNavigate={setActivePayrollTab}/>}
        {activePayrollTab==='Reconciliation'&&<PayrollReconciliationPanel
          periodStart={periodStartKey} periodEnd={periodEndKey} latestActualDate={latestActualDate}
          forecast={forecast} current={periodIsCurrent} scheduleReady={scheduleEvidenceReady}
          unlocked={payrollUnlocked} selectedStoreId={String(store?.value||'ALL')}
          storeLabels={Object.fromEntries(stores.filter(x=>x.value!=='ALL').map(x=>[String(x.value),x.label]))}
          canEdit={payrollCanEdit}
          proposeCarryForward={(payload)=>proposeCarryForwardRef.current
            ? proposeCarryForwardRef.current(payload)
            : Promise.reject(new Error('Payroll is locked. Unlock again in Settings.'))}
          reconcileForecastSnapshot={(payload)=>reconcileForecastSnapshotRef.current
            ? reconcileForecastSnapshotRef.current(payload)
            : Promise.reject(new Error('Payroll is locked. Unlock again in Settings.'))} />}
        {activePayrollTab==='Carry-forwards'&&<PayrollCarryForwardPanel
          unlocked={payrollUnlocked} canEdit={payrollCanEdit} canApprove={payrollIsOwner}
          selectedStoreId={String(store?.value||'ALL')}
          storeLabels={Object.fromEntries(stores.filter(x=>x.value!=='ALL').map(x=>[String(x.value),x.label]))}
          listCases={(payload)=>listCarryForwardCasesRef.current?listCarryForwardCasesRef.current(payload):Promise.reject(new Error('Unlock Payroll in Settings.'))}
          getAudit={(payload)=>getCarryForwardAuditRef.current?getCarryForwardAuditRef.current(payload):Promise.reject(new Error('Unlock Payroll in Settings.'))}
          reviewCase={(payload)=>reviewCarryForwardCaseRef.current?reviewCarryForwardCaseRef.current(payload):Promise.reject(new Error('Unlock Payroll in Settings.'))}
          markIncluded={(payload)=>markCarryForwardIncludedRef.current?markCarryForwardIncludedRef.current(payload):Promise.reject(new Error('Unlock Payroll in Settings.'))}
          recordSettlement={(payload)=>recordCarryForwardSettlementRef.current?recordCarryForwardSettlementRef.current(payload):Promise.reject(new Error('Unlock Payroll in Settings.'))} />}
        {activePayrollTab==='Earnings'&&<div className={panel}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-lg font-bold">Employee earnings and hours</h2><p className="text-xs text-slate-400 mt-1">Draft earnings from approved hours and employer-selected multipliers. PAYE/UIF and finalisation are not active.</p></div><div className="flex flex-wrap gap-2"><button onClick={doPdf} disabled={!payrollUnlocked||!periodIsCurrent} className="rounded-lg border border-white/20 px-3 py-2 text-sm disabled:opacity-40">Export draft PDF</button><button onClick={doExcel} disabled={!payrollUnlocked||!periodIsCurrent} className="rounded-lg border border-white/20 px-3 py-2 text-sm disabled:opacity-40">Export draft Excel</button><button onClick={()=>payrollUnlocked&&periodIsCurrent&&csvRef.current?.link.click()} disabled={!payrollUnlocked||!periodIsCurrent} className="rounded-lg border border-white/20 px-3 py-2 text-sm disabled:opacity-40">CSV</button></div></div>
          <label className="mb-3 block text-xs text-slate-300 sm:max-w-xs">Find employee<input type="search" className="mt-1 w-full rounded-lg border border-white/15 bg-[#07172b] px-3 py-2 text-white" placeholder="Search staff…" value={employeeSearch} onChange={e=>setEmployeeSearch(e.target.value)}/></label>
          <p className="mb-3 text-xs text-slate-400">Employee and site stay visible as you scroll horizontally. Total paid hours includes ordinary, Sunday, public holiday, approved overtime and paid leave hours (without multiplying hours by premium rates). Monthly reference hours are not an automatic second overtime payment. Select <strong>Details</strong> for the earnings breakdown and review notes.</p>
          <div className="max-w-full overflow-x-auto rounded-xl border border-white/10" tabIndex={0} aria-label="Employee earnings table, scroll horizontally to see all payroll columns"><table className="w-[1392px] table-fixed text-sm"><thead className="bg-[#132a46] text-xs text-slate-300"><tr><th className="sticky left-0 z-30 w-[180px] bg-[#132a46] px-3 py-3 text-left">Employee</th><th className="sticky left-[180px] z-30 w-[130px] border-r border-white/20 bg-[#132a46] px-3 py-3 text-left">Site</th><th className="w-[112px] px-3 py-3 text-right">{payHeading('Ordinary h','1.00× base')}</th><th className="w-[112px] px-3 py-3 text-right">{payHeading('Sunday h',`+${factor(Number(earningRules.sundayMultiplier)-1)}× extra`)}</th><th className="w-[112px] px-3 py-3 text-right">{payHeading('Holiday h',`${factor(earningRules.publicHolidayMultiplier)}× total`)}</th><th className="w-[112px] px-3 py-3 text-right">{payHeading('Approved OT h',`${factor(earningRules.overtimeMultiplier)}× total`)}</th><th className="w-[112px] px-3 py-3 text-right">{payHeading('Paid leave h',`${factor(earningRules.paidLeaveMultiplier)}× total`)}</th><th className="w-[112px] px-3 py-3 text-right">Total paid h</th><th className="w-[125px] px-3 py-3 text-right">Hourly rate</th><th className="w-[155px] px-3 py-3 text-right">Draft gross</th><th className="w-[130px] px-3 py-3 text-center">Review</th></tr></thead><tbody className="divide-y divide-white/10">
            {shownSummary.length===0?<tr><td colSpan={11} className="p-5 text-slate-400">No earnings found for this period. Select dates and press Load.</td></tr>:shownSummary.map(r=>{
              const calculated=calculatedByKey[r.key];
              const pay=payrollUnlocked?calculated?.pay:null;
              const flags=[...(r.reviewReasons||[]),...(pay?.hasUnclassifiedAdjustment?['Unclassified legacy adjustment']:[])];
              if(financialDataReady&&(!pay||!pay.ready))flags.push(pay?.reason||'Missing financial profile');
              if(financialDataReady&&contractualExcess(r)>0){
                const review=contractualReviews[r.key];
                if(!isCurrentContractualReview(r,review))flags.push(`Pending contractual overtime review: ${num(contractualExcess(r))}h above the reference.`);
                else if(review.status==='approved')flags.push('Excess reviewed: approved for reconciliation; shift-level allocation and premium remain outstanding.');
                else flags.push('Excess reviewed: rejected as overtime; confirm ordinary wages and statutory entitlements.');
              }
              return <tr key={r.key} className="hover:bg-white/[.035]"><td className="sticky left-0 z-10 w-[180px] bg-[#0d223d] px-3 py-3 font-semibold">{r.name}{!r.staffId&&<div className="text-xs font-normal text-amber-200">Unlinked staff ID</div>}</td><td className="sticky left-[180px] z-10 w-[130px] border-r border-white/20 bg-[#0d223d] px-3 py-3 text-xs text-slate-300">{stores.find(s=>String(s.value)===String(r.storeId))?.label||r.storeId||'—'}</td><td className="px-3 py-3 text-right tabular-nums">{num(r.monSatHrs)}</td><td className="px-3 py-3 text-right tabular-nums">{num(r.sundayHours)}</td><td className="px-3 py-3 text-right tabular-nums">{num(r.publicHours)}</td><td className="px-3 py-3 text-right tabular-nums">{num(r.overtimeHrs)}</td><td className="px-3 py-3 text-right tabular-nums">{num(r.paidLeaveHrs)}</td><td className="px-3 py-3 text-right font-semibold tabular-nums">{num(r.totalHrs)}</td><td className="px-3 py-3 text-right tabular-nums">{pay?.ready?`${payrollCurrency} ${money(pay.hourlyRate)}`:'—'}</td><td className="px-3 py-3 text-right font-semibold tabular-nums">{pay?.ready?`${payrollCurrency} ${money(pay.grossBeforeStatutory)}`:'—'}</td><td className="px-2 py-3 text-center"><div className="flex flex-wrap justify-center gap-1"><button type="button" onClick={()=>{setEarningsDetail(r.key);setContractualReviewInput({status:'approved',reason:''});setContractualReviewMessage('');}} className="rounded-lg border border-white/20 px-2 py-2 text-xs text-cyan-200">Details</button>{flags.length>0&&<button type="button" onClick={()=>setExceptionReviewKey(r.key)} className="rounded-lg border border-amber-400/40 px-2 py-2 text-xs text-amber-200">Investigate ({flags.length})</button>}</div></td></tr>;
            })}</tbody></table></div>
            {payrollUnlocked&&<p className="mt-3 text-xs text-amber-100">Draft gross includes ordinary pay, Sunday base + additional premium, approved overtime, public-holiday pay, paid leave and saved earning items. Paid leave is its own bucket and is not added to worked hours twice. Future scheduled leave is displayed in the employee details, but excluded from accrued draft gross until the leave date. It does not include PAYE/UIF. Overnight shifts and Sunday holidays require manual review.</p>}

        </div>}
        {activePayrollTab==='Deductions'&&<div className={panel}>
          <div className="mb-4"><h2 className="text-lg font-bold">Deductions & statutory calculations</h2><p className="mt-1 text-sm text-slate-400">Review authorised payroll items separately from SARS tax and UIF. These are draft amounts, not approved take-home pay.</p></div>
          <div role="tablist" aria-label="Deduction categories" className="mb-5 flex flex-wrap gap-2">
            <button type="button" role="tab" aria-selected={deductionsSubTab==='items'} className={`rounded-lg px-4 py-2 text-sm font-semibold ${deductionsSubTab==='items'?'bg-cyan-600 text-white':'border border-white/15 bg-[#07172b] text-slate-300'}`} onClick={()=>setDeductionsSubTab('items')}>Employee deductions & allowances</button>
            <button type="button" role="tab" aria-selected={deductionsSubTab==='statutory'} className={`rounded-lg px-4 py-2 text-sm font-semibold ${deductionsSubTab==='statutory'?'bg-cyan-600 text-white':'border border-white/15 bg-[#07172b] text-slate-300'}`} onClick={()=>setDeductionsSubTab('statutory')}>PAYE & UIF</button>
          </div>
          {deductionsSubTab==='items'&&<div><h3 className="mb-1 font-semibold">Employee earnings & non-statutory deductions</h3><p className="mb-3 text-xs text-slate-400">Record allowances, bonuses and authorised deductions. These are not automatically treated as tax deductions.</p>
          {store?.value==='ALL'&&<p className="mb-3 rounded-lg bg-amber-400/10 p-3 text-xs text-amber-100">Select one site to load, edit or include payroll line items. All Stores does not consolidate items in PR2.</p>}
          <div className="overflow-x-auto rounded-xl border border-white/10"><table className="w-full min-w-[790px] text-sm"><thead className="bg-[#132a46]"><tr><th className="p-3 text-left">Employee</th><th className="p-3 text-right">Extra earnings</th><th className="p-3 text-right">Listed deductions</th><th className="p-3 text-right">Unclassified old adj.</th><th className="p-3 text-right">Action</th></tr></thead><tbody className="divide-y divide-white/10">{shownSummary.map(r=>{
            const pay=payrollUnlocked?calculatedByKey[r.key]?.pay:null;
            return <tr key={r.key}><td className="p-3 font-medium">{r.name}</td><td className="p-3 text-right">{pay?.ready?`${payrollCurrency} ${money(pay.earningsItems)}`:'—'}</td><td className="p-3 text-right">{pay?.ready?`${payrollCurrency} ${money(pay.deductions)}`:'—'}</td><td className="p-3 text-right text-amber-200">{payrollUnlocked&&Math.abs(Number(adj[r.key])||0)>0?`${payrollCurrency} ${money(adj[r.key])}`:'—'}</td><td className="p-3 text-right"><button type="button" disabled={!payrollUnlocked||!payrollCanEdit||store?.value==='ALL'||!r.staffId} onClick={()=>openItemModal(r)} className="rounded-lg bg-cyan-600 px-3 py-2 text-xs disabled:opacity-40">Edit items</button></td></tr>;
          })}{shownSummary.length===0&&<tr><td colSpan={5} className="p-4 text-slate-400">Load employees to review draft line items.</td></tr>}</tbody></table></div>
          <p className="mt-3 text-xs text-amber-200">Every deduction requires a documented authorisation or other lawful basis. Recording a reference does not by itself establish that a deduction is lawful. Payroll must verify before paying.</p>
          </div>}
          {deductionsSubTab==='statutory'&&<PayrollStatutoryPreview key={selectedFingerprint} rows={calculatedRows} profilesByKey={profilesByKey} retirementDrafts={retirementDrafts} siteLabels={Object.fromEntries(stores.filter(s=>s.value!=='ALL').map(s=>[String(s.value),s.label]))}
            periodStart={localDay(dateFrom)} periodEnd={localDay(dateTo)}
            unlocked={financialDataReady} canEdit={payrollCanEdit}
            saveTaxProfile={(payload)=>saveTaxProfileRef.current?saveTaxProfileRef.current(payload):Promise.reject(Error('Unlock Payroll in Settings again.'))}
            saveRetirementDraft={(payload)=>saveRetirementDraftRef.current?saveRetirementDraftRef.current(payload):Promise.reject(Error('Unlock Payroll in Settings again.'))}
            previewStatutory={(payload)=>previewStatutoryRef.current?previewStatutoryRef.current(payload):Promise.reject(Error('Unlock Payroll in Settings again.'))}/>}
        </div>}
        {activePayrollTab==='Payslips'&&<div className={panel}><h2 className="text-lg font-bold">Payslip preparation</h2><p className="mt-2 text-sm text-slate-300">Payslip generation, publication dates, early release and private staff access are scheduled after payroll approval and locking (PR4–PR5).</p><div className="mt-4 rounded-lg border border-amber-300/20 bg-amber-400/10 p-3 text-sm text-amber-100">No payslips can be published from PR1.1. Do not treat draft exports as employee payslips.</div></div>}
        {activePayrollTab==='History'&&<div className={panel}><h2 className="text-lg font-bold">Payroll history & archived staff</h2><p className="mt-1 text-sm text-slate-300">Finalised, locked payroll runs will appear here in PR4. Existing payroll-only archive controls are retained below.</p>
          <div className="mt-4 rounded-xl border border-white/15 bg-white/[.035] p-4 text-sm text-slate-200">
            <div className="font-semibold">Staff offboarding and payroll history</div>
            <p className="mt-2 text-xs text-slate-300">Offboard a departing employee once in Neo Admin → Staff. Their past shifts and paid leave remain in the payroll period when earned. In future months with no activity, they do not appear in period earnings. Neo does not delete or hide past payroll solely because an employee is archived.</p>
            <p className="mt-2 text-xs text-amber-200">Important: historical payroll-only archives keyed by employee name no longer hide earned wages. Check final leave and pay balances before offboarding.</p>
          </div>
        </div>}
      </div>
      {itemModal.open&&<Modal title={`Draft payroll items · ${itemModal.row?.name||'Employee'}`} onClose={()=>!itemSaving&&setItemModal({open:false,row:null,items:[]})}>
        <div className="max-h-[70vh] overflow-y-auto space-y-3 text-sm">
          <p className="text-xs text-slate-300">These items are period-specific. Do not save deductions without lawful authority. PR3.4 classifies extra earnings for review only; it does not calculate their final PAYE or UIF treatment.</p>
          {itemModal.items.map((item,i)=><div key={item.id} className="rounded-xl border border-white/15 bg-[#0a1b31] p-3 space-y-2">
            <div className="grid gap-2 sm:grid-cols-2"><label className="text-xs">Type<select className="mt-1 w-full rounded-lg bg-[#10243b] border border-white/15 px-2 py-2" value={item.kind} onChange={e=>editItem(i,{kind:e.target.value,category:LINE_ITEM_CATEGORIES[e.target.value][0].value,authorizationReference:'',taxTreatment:'pending',taxEvidenceReference:''})}><option value="earning">Earning / allowance</option><option value="deduction">Authorised deduction</option></select></label>
            <label className="text-xs">Category<select className="mt-1 w-full rounded-lg bg-[#10243b] border border-white/15 px-2 py-2" value={item.category} onChange={e=>editItem(i,{category:e.target.value,taxTreatment:'pending',taxEvidenceReference:''})}>{LINE_ITEM_CATEGORIES[item.kind].map(c=><option key={c.value} value={c.value}>{c.label}</option>)}</select></label></div>
            <label className="block text-xs">Amount ({payrollCurrency})<input className="mt-1 w-full rounded-lg bg-[#10243b] border border-white/15 px-3 py-2" type="number" step="0.01" min="0.01" value={item.amount} onChange={e=>editItem(i,{amount:e.target.value})}/></label>
            <label className="block text-xs">Description<input className="mt-1 w-full rounded-lg bg-[#10243b] border border-white/15 px-3 py-2" maxLength={160} value={item.description||''} onChange={e=>editItem(i,{description:e.target.value})}/></label>
            {item.kind==='earning'&&<div className="rounded-lg border border-amber-300/20 bg-amber-400/5 p-3 space-y-2">
              <label className="block text-xs">PR3.4 · Special earning classification<select className="mt-1 w-full rounded-lg bg-[#10243b] border border-white/15 px-2 py-2" value={item.taxTreatment||'pending'} onChange={e=>editItem(i,{taxTreatment:e.target.value,taxEvidenceReference:''})}>
                {(SPECIAL_EARNING_CLASSIFICATIONS[item.category]||SPECIAL_EARNING_CLASSIFICATIONS['other-earning']).map(option=><option key={option.value} value={option.value}>{option.label}</option>)}
              </select></label>
              {item.taxTreatment&&item.taxTreatment!=='pending'&&<label className="block text-xs">Tax treatment review/evidence reference (8+ characters)<input className="mt-1 w-full rounded-lg bg-[#10243b] border border-white/15 px-3 py-2" maxLength={160} value={item.taxEvidenceReference||''} onChange={e=>editItem(i,{taxEvidenceReference:e.target.value})} placeholder="Payroll review / expense evidence / supporting document"/></label>}
              <p className="text-xs text-amber-100">This records a review category only. A bonus, allowance or reimbursement still blocks provisional PAYE/UIF until reconciled; classification does not approve tax treatment.</p>
            </div>}
            {item.kind==='deduction'&&<label className="block text-xs">Written agreement / statutory authorisation reference (required)<input className="mt-1 w-full rounded-lg bg-[#10243b] border border-white/15 px-3 py-2" maxLength={160} value={item.authorizationReference||''} onChange={e=>editItem(i,{authorizationReference:e.target.value})}/></label>}
            <div className="flex justify-end"><button type="button" className="text-xs rounded-lg border border-rose-500/40 px-3 py-2 text-rose-200" onClick={()=>removeItem(i)}>Remove</button></div>
          </div>)}
          <div className="flex flex-wrap gap-2 justify-between"><button type="button" disabled={itemModal.items.length>=12||itemSaving} onClick={addItem} className="rounded-lg border border-cyan-400/50 px-3 py-2 text-cyan-100 disabled:opacity-40">+ Add line item</button><div className="flex gap-2"><button type="button" disabled={itemSaving} onClick={()=>setItemModal({open:false,row:null,items:[]})} className="rounded-lg bg-slate-700 px-4 py-2">Cancel</button><button type="button" disabled={itemSaving} onClick={saveItems} className="rounded-lg bg-cyan-600 px-4 py-2 font-semibold disabled:opacity-40">{itemSaving?'Saving…':'Save items'}</button></div></div>
          {itemError&&<p role="alert" className="text-xs text-rose-200">{itemError}</p>}
        </div>
      </Modal>}
      {exceptionReviewKey&&(()=>{
        const row=periodIsCurrent?summary.find(item=>item.key===exceptionReviewKey):null;
        if(!row)return null;
        const review=contractualReviews[row.key];
        return <PayrollExceptionReviewDrawer row={row} financeUnlocked={financialDataReady} periodCurrent={periodIsCurrent}
          from={periodStartKey} to={periodEndKey} acceptedDays={acceptedPayrollDays}
          publishedEntries={publishedForecastEntries} contractualReview={isCurrentContractualReview(row,review)?review:null}
          contractualExcess={financialDataReady?contractualExcess(row):0}
          loadEvidence={payload=>{if(!getExceptionEvidenceRef.current)throw new Error('Unlock Payroll to load evidence.');return getExceptionEvidenceRef.current(payload);}}
          requestVerification={payload=>{if(!requestOperationalVerificationRef.current)throw new Error('Unlock Payroll before requesting manager verification.');return requestOperationalVerificationRef.current(payload);}}
          listVerifications={payload=>{if(!listOperationalVerificationRef.current)throw new Error('Unlock Payroll before reading verification status.');return listOperationalVerificationRef.current(payload);}}
          onClose={()=>setExceptionReviewKey(null)} onOpenEarnings={()=>{setExceptionReviewKey(null);setContractualReviewInput({status:'approved',reason:''});setContractualReviewMessage('');setEarningsDetail(row.key);}}/>;
      })()}
      {earningsDetail&&(()=>{
        const row=summary.find(x=>x.key===earningsDetail);
        const pay=payrollUnlocked?calculatedByKey[earningsDetail]?.pay:null;
        if(!row)return null;
        const flags=[...(row.reviewReasons||[]),...(pay?.hasUnclassifiedAdjustment?['Unclassified legacy adjustment excluded']:[])];
        if(contractualExcess(row)>0){
          const review=contractualReviews[row.key];
          flags.push(isCurrentContractualReview(row,review)?
            `Contractual excess reviewed (${review.status}), but premium classification and source shifts still require reconciliation.`:
            `Pending contractual overtime review: ${num(contractualExcess(row))}h; no extra amount added.`);
        }
        return <Modal title={`Earnings breakdown · ${row.name}`} onClose={()=>setEarningsDetail(null)}>
          <div className="max-h-[70vh] overflow-y-auto space-y-4 text-sm text-white">
            <p className="text-xs text-amber-100">DRAFT only, before PAYE/UIF. Monthly reference excess must be reviewed against the contract and approvals, not automatically paid again. Review overnight, Sunday and holiday allocation before payment.</p>
            <div className="rounded-lg border border-white/10 bg-[#10243b] p-3 space-y-2">
              {[['Ordinary',row.monSatHrs,pay?.ordinary],['Sunday (base + premium)',row.sundayHours,pay?.sunday],['Public holidays',row.publicHours,pay?.holiday],['Approved overtime',row.overtimeHrs,pay?.overtime],['Paid leave earned to date',row.paidLeaveHrs,pay?.leave]].map(([label,hrs,value])=><div key={label} className="flex justify-between gap-3 border-b border-white/10 py-1"><span>{label} · {num(hrs)}h</span><span className="tabular-nums">{pay?.ready?`${payrollCurrency} ${money(value)}`:'—'}</span></div>)}
              {row.scheduledFutureLeaveHrs>0&&<div className="flex justify-between gap-3 py-1 text-amber-200"><span>Upcoming scheduled leave (NOT earned)</span><span>{num(row.scheduledFutureLeaveHrs)}h</span></div>}
              <div className="flex justify-between gap-3 border-t border-white/20 pt-2 font-semibold"><span>Total paid hours (includes paid leave)</span><span className="tabular-nums">{num(row.totalHrs)}h</span></div>
              <div className="rounded-lg border border-cyan-400/20 bg-cyan-400/5 p-3 text-xs text-slate-200">
                <div className="font-semibold text-cyan-200 mb-1">Contractual monthly reference check</div>
                <p>Reference: {num(Number(profilesByKey[`${row.storeId}__${row.staffId}`]?.referenceHours)||Number(totalHours)||180)}h · Already approved overtime: {num(row.overtimeHrs)}h</p>
                <p className="mt-1">Additional non-overtime payable hours above the reference: {num(contractualExcess(row))}h{contractualExcess(row)>0?' — manager must review and approve reclassification.':' — none flagged.'}</p>
                <p className="mt-1 text-amber-200">An approved overtime hour is counted once. The reference is not an automatic overtime payment trigger.</p>
              </div>
              {contractualExcess(row)>0&&<div className="rounded-lg border border-amber-400/25 bg-amber-400/5 p-3 space-y-3 text-xs">
                <h4 className="font-bold text-amber-200">Contractual overtime decision · PR2.4</h4>
                <p>{num(contractualExcess(row))}h beyond the monthly reference is pending review. Existing approved overtime ({num(row.overtimeHrs)}h) is separate and will not be counted again.</p>
                {(() => {const review=contractualReviews[row.key];return review&&isCurrentContractualReview(row,review)?
                  <div className="rounded border border-cyan-400/25 bg-[#112a3e] p-2">Last decision: <strong>{review.status==='approved'?'Approved for payroll reconciliation':'Rejected as overtime'}</strong> · {review.reviewedAt?.slice(0,16)?.replace('T',' ')||'—'} · {review.reason}<div className="mt-1 text-amber-200">No new overtime premium is posted automatically. Payroll must reconcile and allocate source shifts before finalisation.</div></div>:
                  <p className="text-amber-200">{review?'Previous decision is stale because hours or contractual reference changed. Review again.':'Not yet reviewed for this payroll period.'}</p>;})()}
                {payrollCanEdit&&payrollUnlocked&&<>
                  <label className="block">Decision
                    <select value={contractualReviewInput.status} onChange={e=>setContractualReviewInput(v=>({...v,status:e.target.value}))} className="mt-1 block w-full rounded-lg border border-white/20 bg-[#07172b] p-2">
                      <option value="approved">Approve for reconciliation (no automatic premium)</option>
                      <option value="rejected">Reject as overtime (retain payable ordinary hours)</option>
                    </select>
                  </label>
                  <label className="block">Reason and attendance evidence reviewed
                    <textarea rows={3} maxLength={500} value={contractualReviewInput.reason} onChange={e=>setContractualReviewInput(v=>({...v,reason:e.target.value}))} className="mt-1 block w-full rounded-lg border border-white/20 bg-[#07172b] p-2" placeholder="What shifts, approvals or contract terms support this decision?" />
                  </label>
                  <button type="button" disabled={contractualReviewBusy||!financialDataReady||!row.staffId} onClick={()=>saveExcessReview(row)} className="rounded-lg bg-cyan-600 px-3 py-2 font-semibold disabled:opacity-40">{contractualReviewBusy?'Saving decision…':'Record overtime decision'}</button>
                  {contractualReviewMessage&&<p role="status" aria-live="polite" className="text-cyan-200">{contractualReviewMessage}</p>}
                </>}
              </div>}
              <div className="flex justify-between gap-3 pt-1 font-bold"><span>Draft gross</span><span className="tabular-nums">{pay?.ready?`${payrollCurrency} ${money(pay.grossBeforeStatutory)}`:'—'}</span></div>
            </div>
            <div className="rounded-lg border border-amber-300/20 p-3"><div className="font-semibold text-amber-200">Review notes</div>{flags.length?<ul className="mt-2 list-disc pl-5 text-sm">{flags.map((x,i)=><li key={i}>{x}</li>)}</ul>:<p className="mt-2 text-sm text-slate-300">No automatic review flags for this row.</p>}</div>
            <button type="button" className="rounded-lg border border-white/20 px-4 py-2" onClick={()=>setEarningsDetail(null)}>Close</button>
          </div>
        </Modal>;
      })()}
      {payrollDetailModal.open ? (
        <Modal
          title={`Payroll details • ${payrollDetailModal.name}`}
          onClose={() => setPayrollDetailModal({ open: false, key: '', name: '', adjustment: '', note: '' })}
        >
          <div className="space-y-4 text-sm">
            <div>
              <label className="mb-1 block font-medium">Deduction / Extras (R)</label>
              <input
                type="number"
                step="0.01"
                value={payrollDetailModal.adjustment}
                onChange={(e) => setPayrollDetailModal((m) => ({ ...m, adjustment: e.target.value }))}
                placeholder="0.00"
                className="w-full rounded border border-white/10 bg-[#07172b] px-3 py-2 text-white"
              />
              <div className="mt-1 text-xs text-white/50">Use a negative amount for a deduction and a positive amount for an extra.</div>
            </div>

            <div>
              <label className="mb-1 block font-medium">Note</label>
              <textarea
                value={payrollDetailModal.note}
                onChange={(e) => setPayrollDetailModal((m) => ({ ...m, note: e.target.value }))}
                placeholder="Optional payroll note..."
                className="min-h-[120px] w-full rounded border border-white/10 bg-[#07172b] px-3 py-2 text-white"
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setPayrollDetailModal({ open: false, key: '', name: '', adjustment: '', note: '' })}
                className="rounded bg-slate-700 px-4 py-2 hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={savePayrollDetails}
                className="rounded bg-cyan-500 px-4 py-2 font-semibold text-slate-950 hover:bg-cyan-400"
              >
                Save
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      <CSVLink ref={csvRef} data={csvData} filename="payroll-PR2-DRAFT-not-for-payment.csv" className="hidden" />
    </div>
  );
}
